import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Tooltip,
  Collapse,
  Divider,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table as MuiTable,
  TableHead as MuiTableHead,
  TableBody as MuiTableBody,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Add, Delete, AttachFile, ExpandMore, ExpandLess, Refresh, FilterList, Clear, Search, Upload, Download, Edit, PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { downloadAuthenticatedPdf } from '../../utils/authenticatedPdf';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import {
  RECEIVING_PRIMARY_CONDITIONS,
  LEGACY_PART_CONDITIONS,
  PART_CONDITION_LABELS,
  formatPartConditionLabel,
  partConditionSelectValue,
  deriveReceivingLineCondition,
  receivingUnitDetailCount
} from '../../constants/partUnitConditions';
import { STORE_TYPES, normalizeStoreTypeValue, formatStoreTypeLabel } from '../../constants/storeTypes';
import { unitMetricLabel, unitSelectOptions } from '../../constants/unitMetrics';
import { normalizeInventoryCategory } from '../../constants/inventoryCategories';
import { normalizePartNumber } from '../../utils/partNumber';
import { getErrorMessage } from '../../utils/apiErrorUtils';
import StoreDocumentsDialog from './StoreDocumentsDialog';
import PurchaseOrderDeleteDialog from './PurchaseOrderDeleteDialog';
import ReceivingItemsGrid, { createEmptyReceivingItem } from './ReceivingItemsGrid';
import InventoryItemFormDialog from './InventoryItemFormDialog';
import CertificateField from './CertificateField';
import {
  findReceivingCertificateDocuments,
  buildReceivingDocumentCategory,
  downloadReceivingCertificate
} from '../../utils/storeCertificateDocuments';
import LoanReceivingMetaFields from './LoanReceivingMetaFields';
import {
  buildOutstandingReturnLines,
  buildReceivingItemsForLoanRecord,
  validateLoanReturnItemsForSubmit
} from '../../utils/loanReceivingProfiles';
import {
  deriveLineBatchNumber,
  formatReceivingLocation,
  hydrateReceivingLineForForm,
  normalizeLineSerialNumberDisplay,
  normalizeReceivingLocation,
  resolveLineSerialNumber,
  syncLineSerialBatchIntoUnitDetails,
  unitExpiryForPersistence,
  withLineStoreTypeOnUnits
} from '../../utils/inventoryItemForm';
import { isPlaceholderSerialNumber } from '../../utils/partMasterLabel';
import {
  historyRowLoanPurposeChip,
  loanReceivingPurposeChipColor,
  loanReceivingPurposeLabel,
  matchesReceivingContext,
  receivingContextBannerSeverity,
  receivingContextFormDescription,
  receivingContextFormTitle,
  receivingContextHistoryLabel,
  receivingContextSubmitLabel,
  receivingHistoryReferenceColumnLabel
} from '../../utils/receivingContextFilter';

const PO_REFERENCE_FIELDS = [
  { key: 'dnNo', label: 'DN No', docCategory: 'PO_DN' },
  { key: 'awbNo', label: 'AWB No', docCategory: 'PO_AWB' },
  { key: 'boeNo', label: 'BOE No', docCategory: 'PO_BOE' },
  { key: 'invoiceNo', label: 'Invoice No', docCategory: 'PO_INVOICE' }
];
const SHIPMENT_FIELDS = [
  { key: 'dnNo', label: 'DN No', docsKey: 'dnDocuments' },
  { key: 'awbNo', label: 'AWB No', docsKey: 'awbDocuments' },
  { key: 'boeNo', label: 'BOE No', docsKey: 'boeDocuments' },
  { key: 'invoiceNo', label: 'Invoice No', docsKey: 'invoiceDocuments' }
];

const INSPECT_APPROVE_ROLES = [ROLES.STORE_INSPECTOR, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN];
const RECEIVING_CONDITION_SELECT_VALUES = [...RECEIVING_PRIMARY_CONDITIONS, ...LEGACY_PART_CONDITIONS];
// Legacy constant retained for potential future use; autosave behavior is disabled in favor of manual drafts.
const AUTOSAVE_DELAY_MS = 1200;

const shelfToInputValue = (v) => {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const CSV_COLUMNS = [
  'aircraftId',
  'aircraftRegistration',
  'purchaseOrderNumber',
  'receivingRecordId',
  'receivingDate',
  'supplierCode',
  'supplierName',
  'partNumber',
  'referencePartNumber',
  'description',
  'category',
  'orderedQty',
  'receivedQty',
  'unit',
  'condition',
  'certificateNumber',
  'batchNumber',
  'serialNumber',
  'shelfLifeUpto',
  'remarks',
  'defaultWarehouse',
  'defaultShelf',
  'defaultBin'
];

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const parseCsvLine = (line) => {
  const out = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  out.push(current);
  return out;
};

const normalizeReceivingRecords = (records) =>
  (Array.isArray(records) ? records : [])
    .filter((record) => record && typeof record === 'object')
    .map((record) => ({
      ...record,
      items: Array.isArray(record.items) ? record.items : [],
      statusHistory: Array.isArray(record.statusHistory) ? record.statusHistory : []
    }));

/** PO number for normal receiving; loan reference when loanDirection is LOAN_IN (store loan-in). */
function recordProcurementReference(record) {
  if (record?.loanDirection === 'LOAN_IN') {
    return String(record.loanReferenceNumber || '').trim() || '—';
  }
  return record?.purchaseOrderNumber || '—';
}

function mapPoLinesToReceivingItems(lineItems = []) {
  return lineItems.map((l) => ({
    partNumber: l.partNumber,
    referencePartNumber: l.referencePartNumber || '',
    description: l.description || '',
    quantity: l.quantity,
    unit: l.unit || 'EACH',
    alreadyReceived: l.receivedQuantity ?? 0,
    receivedQuantity: 0,
    poLineId: l._id,
    poLineNumber: l.lineNumber,
    lineStatus: l.lineStatus || 'OPEN',
    closureRemarks: l.closureRemarks || '',
    condition: 'SERVICEABLE',
    category: normalizeInventoryCategory(l.category),
    certificateNumber: '',
    batchNumber: '',
    serialNumber: '',
    unitDetails: [],
    storeType: '',
    storePartCode: '',
    locationText: '',
    location: { warehouse: '', shelf: '', bin: '' },
    reorderPoint: 0,
    minimumStock: 0,
    remarks: '',
    shelfLifeUpto: '',
    shelfLifeNotApplicable: false
  }));
}

/** Match a saved receiving line back onto a fresh PO line (id → line # → part). */
function findExistingReceivingItemForPoLine(existingItems, freshPoLine) {
  const existing = Array.isArray(existingItems) ? existingItems : [];
  if (freshPoLine?.poLineId) {
    const byId = existing.find((item) => String(item.poLineId || '') === String(freshPoLine.poLineId));
    if (byId) return byId;
  }
  const lineNumber = Number(freshPoLine?.poLineNumber);
  if (Number.isFinite(lineNumber)) {
    const byNumber = existing.find((item) => Number(item.poLineNumber) === lineNumber);
    if (byNumber) return byNumber;
  }
  const partKey = normalizePartNumber(freshPoLine?.partNumber).toLowerCase();
  if (partKey) {
    return existing.find(
      (item) => normalizePartNumber(item.partNumber).toLowerCase() === partKey
    );
  }
  return null;
}

/** Keep draft/detail fields when PO lines are reloaded for the same order. */
function mergePoLinesWithExistingReceivingItems(lineItems = [], existingItems = []) {
  const mapped = mapPoLinesToReceivingItems(lineItems);
  return mapped.map((fresh) => {
    const prior = findExistingReceivingItemForPoLine(existingItems, fresh);
    if (!prior) return fresh;
    return {
      ...fresh,
      receivedQuantity: Number(prior.receivedQuantity) || 0,
      condition: prior.condition || fresh.condition,
      category: prior.category || fresh.category,
      unit: String(prior.unit || '').trim() || fresh.unit,
      certificateNumber: prior.certificateNumber || '',
      batchNumber: prior.batchNumber || '',
      serialNumber: prior.serialNumber || '',
      serialNumbers: Array.isArray(prior.serialNumbers) ? prior.serialNumbers : [],
      unitDetails: Array.isArray(prior.unitDetails) ? prior.unitDetails : [],
      storeType: normalizeStoreTypeValue(prior.storeType) || '',
      storePartCode: prior.storePartCode || '',
      locationText: prior.locationText || '',
      location: normalizeReceivingLocation(prior.location),
      reorderPoint: prior.reorderPoint ?? fresh.reorderPoint,
      minimumStock: prior.minimumStock ?? fresh.minimumStock,
      remarks: prior.remarks || '',
      shelfLifeUpto: prior.shelfLifeUpto || '',
      shelfLifeNotApplicable: Boolean(prior.shelfLifeNotApplicable),
      aircraftScope: prior.aircraftScope || fresh.aircraftScope,
      aircraftId: prior.aircraftId || fresh.aircraftId,
      aircraftRegistration: prior.aircraftRegistration || fresh.aircraftRegistration
    };
  });
}

function findPoLineForReceivingItem(poLineItems, item) {
  const lines = Array.isArray(poLineItems) ? poLineItems : [];
  if (item?.poLineId) {
    const byId = lines.find((line) => String(line._id) === String(item.poLineId));
    if (byId) return byId;
  }
  const lineNumber = Number(item?.poLineNumber);
  if (Number.isFinite(lineNumber)) {
    const byNumber = lines.find((line) => Number(line.lineNumber) === lineNumber);
    if (byNumber) return byNumber;
  }
  const partKey = normalizePartNumber(item?.partNumber).toLowerCase();
  if (partKey) {
    return lines.find(
      (line) => normalizePartNumber(line.partNumber).toLowerCase() === partKey
    );
  }
  return null;
}

function syncReceivingItemUnitsFromPoLines(items, poLineItems = []) {
  return (items || []).map((item) => {
    const poLine = findPoLineForReceivingItem(poLineItems, item);
    if (!poLine) return item;
    // Preserve unit already saved on the receiving line (Review / Continue draft).
    const existingUnit = String(item.unit || '').trim();
    return {
      ...item,
      unit: existingUnit || poLine.unit || 'EACH',
      // Runtime PO fields are not persisted on drafts — restore on load.
      alreadyReceived: poLine.receivedQuantity ?? item.alreadyReceived ?? 0,
      lineStatus: poLine.lineStatus || item.lineStatus || 'OPEN',
      closureRemarks: poLine.closureRemarks || item.closureRemarks || '',
      poLineId: item.poLineId || poLine._id,
      poLineNumber: item.poLineNumber != null ? item.poLineNumber : poLine.lineNumber
    };
  });
}

/**
 * Hydrate saved receiving lines for the form; when linked to a PO, enrich
 * runtime PO fields without wiping draft qty / serial / location details.
 */
async function hydrateReceivingItemsForForm(recordItems, poId) {
  const mappedItems = (recordItems || []).map((line) => hydrateReceivingLineForForm(line));
  if (!poId) return mappedItems;
  try {
    const poLineItems = await fetchPoLineItems(poId);
    if (mappedItems.length === 0 && poLineItems.length > 0) {
      // Draft saved with PO selected but no lines yet — seed empty PO rows.
      return mapPoLinesToReceivingItems(poLineItems);
    }
    return syncReceivingItemUnitsFromPoLines(mappedItems, poLineItems);
  } catch {
    return mappedItems;
  }
}

function resolveReceivingAircraftId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    const id = value._id ?? value.id;
    return id ? String(id) : '';
  }
  return String(value);
}

async function fetchPoLineItems(poId) {
  if (!poId) return [];
  const res = await axios.get(`/api/store/purchase-orders/${poId}`);
  return res.data?.lineItems || [];
}

function ReceivingModule({
  recordId: linkRecordId,
  recordType,
  inventoryDomain = 'STORE',
  receivingContext = null,
  initialLoanPrefill = null
}) {
  const location = useLocation();
  const storeApi = useCallback(
    (path) => `/api/store${inventoryDomain === 'LOAN' ? '/loan' : ''}${path}`,
    [inventoryDomain]
  );
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState('');
  const [formData, setFormData] = useState({
    supplier: '',
    purchaseOrderNumber: '',
    discrepancies: ''
  });
  const [loanReceivingMeta, setLoanReceivingMeta] = useState({
    counterpartyCompany: '',
    counterpartyContactName: '',
    counterpartyContactPhone: '',
    loanReferenceNumber: '',
    returnDueDate: '',
    loanReceivingPurpose: 'LOAN_IN',
    linkedSourceRecordId: '',
    lockMeta: false
  });
  const [items, setItems] = useState([]);
  const [certificateFilesByRow, setCertificateFilesByRow] = useState({});
  const [loading, setLoading] = useState(false);
  const [documentRecordId, setDocumentRecordId] = useState('');
  const [documentDialog, setDocumentDialog] = useState({
    open: false,
    recordId: '',
    label: ''
  });
  const [lastReceivingSummary, setLastReceivingSummary] = useState(null);
  const [receivingHistory, setReceivingHistory] = useState([]);
  const [loadingReceivingHistory, setLoadingReceivingHistory] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('ALL');
  const [aircraftFilter, setAircraftFilter] = useState('ALL');
  const [selectedReceivingAircraft, setSelectedReceivingAircraft] = useState('GEN');
  const [aircraftList, setAircraftList] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [workflowFilter, setWorkflowFilter] = useState('ALL');
  const [approvalDialog, setApprovalDialog] = useState({
    open: false,
    recordId: null,
    loanReceivingPurpose: null,
    hasPurchaseOrder: false,
    submittedById: null,
    headerAircraft: { scope: 'GEN', id: '', registration: 'GEN' }
  });
  const [approvalLineFormDialog, setApprovalLineFormDialog] = useState({
    open: false,
    rowIndex: null
  });
  const [approvalItems, setApprovalItems] = useState([]);
  const [approvalDiscrepancies, setApprovalDiscrepancies] = useState('');
  const [approvalInspectorNotes, setApprovalInspectorNotes] = useState('');
  const [approvalRejectionNotes, setApprovalRejectionNotes] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [poDocsByPoId, setPoDocsByPoId] = useState({});
  const [poDocBusyByCategory, setPoDocBusyByCategory] = useState({});
  const [poFieldDocsDialog, setPoFieldDocsDialog] = useState({ open: false, poId: '', category: '', label: '' });
  const [downloadingDocKey, setDownloadingDocKey] = useState(null);
  const [poDeleteDialog, setPoDeleteDialog] = useState({ open: false, targets: [] });
  const [selectedHistoryIds, setSelectedHistoryIds] = useState([]);
  const [historyDeleteDialog, setHistoryDeleteDialog] = useState({ open: false, targets: [] });
  const [historyDeleteLoading, setHistoryDeleteLoading] = useState(false);
  const [shipmentDocBusy, setShipmentDocBusy] = useState({});
  const [draftRecordId, setDraftRecordId] = useState('');
  const [editingPendingRecordId, setEditingPendingRecordId] = useState('');
  const [formReceivingDocuments, setFormReceivingDocuments] = useState([]);
  const [downloadingCertificateRowKey, setDownloadingCertificateRowKey] = useState('');
  const [priTagDialog, setPriTagDialog] = useState({ open: false, record: null });
  const [downloadingPriKey, setDownloadingPriKey] = useState('');
  const [formEditMode, setFormEditMode] = useState(null);
  const [formEditRecordRef, setFormEditRecordRef] = useState('');
  const [resubmitOfRecordId, setResubmitOfRecordId] = useState('');
  const formSectionRef = useRef(null);
  const [autosaveState, setAutosaveState] = useState({ saving: false, lastSavedAt: null, error: '' });
  const skipAutosaveRef = useRef(true);
  /** Bumped to ignore stale localStorage draft restores after Continue draft / clear. */
  const loadDraftGenerationRef = useRef(0);
  const { showToast } = useToast();
  const [archiveFieldsDialogOpen, setArchiveFieldsDialogOpen] = useState(false);
  const [archivedHistoryFields, setArchivedHistoryFields] = useState(() => {
    try {
      if (typeof window === 'undefined') return [];
      const raw = window.localStorage.getItem('receivingHistoryArchivedFields');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const isStoreExecutive = user?.role === ROLES.STORE_EXECUTIVE;
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;
  const canDirectReceive = INSPECT_APPROVE_ROLES.includes(user?.role);
  const canInspectApprove = canDirectReceive;
  const canSubmitReceiving = isStoreExecutive || canDirectReceive;
  const isAgainstPoContext = receivingContext === 'AGAINST_PO';
  const isLoanRentalContext = receivingContext === 'LOAN_RENTAL';
  const isFromAircraftContext = receivingContext === 'FROM_AIRCRAFT';
  const contextHidesPurchaseOrder = isLoanRentalContext || isFromAircraftContext;
  const showSubmitWithoutPo = !contextHidesPurchaseOrder && !isAgainstPoContext;
  const isLoanOutReturnReceiving = loanReceivingMeta.loanReceivingPurpose === 'LOAN_OUT_RETURN';
  const isLoanInReturnReceiving = loanReceivingMeta.loanReceivingPurpose === 'LOAN_IN_RETURN';
  const isLoanReturnReceiving = isLoanOutReturnReceiving || isLoanInReturnReceiving;
  const loanUiOptions = {
    isLoanReturn: isLoanReturnReceiving,
    loanPurpose: loanReceivingMeta.loanReceivingPurpose || 'LOAN_IN'
  };
  const showAircraftScopeField = true; // Against PO, From Aircraft, and Loan/Rental
  const historyReferenceColumnLabel = receivingHistoryReferenceColumnLabel(receivingContext);

  const autosaveStorageKey = receivingContext
    ? `receivingDraft:${inventoryDomain}:${receivingContext}:${user?._id || 'anon'}`
    : `receivingDraft:${inventoryDomain}:${user?._id || 'anon'}`;

  const isHistoryFieldArchived = useCallback(
    (fieldKey) => archivedHistoryFields.includes(fieldKey),
    [archivedHistoryFields]
  );

  const isRecordSubmitter = useCallback((record) => {
    const submitter = record?.submittedBy?._id || record?.submittedBy
      || record?.receivedBy?._id || record?.receivedBy;
    return Boolean(submitter && user?._id && String(submitter) === String(user._id));
  }, [user?._id]);

  const resetReceivingForm = useCallback(() => {
    loadDraftGenerationRef.current += 1;
    setSelectedPoId('');
    setSelectedReceivingAircraft('GEN');
    setFormData({
      supplier: suppliers[0]?._id || '',
      purchaseOrderNumber: '',
      discrepancies: ''
    });
    setLoanReceivingMeta({
      counterpartyCompany: '',
      counterpartyContactName: '',
      counterpartyContactPhone: '',
      loanReferenceNumber: '',
      returnDueDate: '',
      loanReceivingPurpose: 'LOAN_IN',
      linkedSourceRecordId: '',
      lockMeta: false
    });
    setItems([]);
    setCertificateFilesByRow({});
    setFormReceivingDocuments([]);
    setDraftRecordId('');
    setEditingPendingRecordId('');
    setFormEditMode(null);
    setFormEditRecordRef('');
    setResubmitOfRecordId('');
    window.localStorage.removeItem(autosaveStorageKey);
  }, [autosaveStorageKey, suppliers]);

  const clearFormEdit = useCallback(() => {
    resetReceivingForm();
    showToast('Form cleared.', 'info');
  }, [resetReceivingForm, showToast]);

  const loadRecordIntoForm = useCallback(async (record, mode) => {
    try {
      if (mode === 'resubmit' && (record.supersededBy || record.resubmittedAt)) {
        showToast('This rejected receiving has already been revised and resubmitted.', 'warning');
        return;
      }
      // Always refetch — history list rows can be stale (e.g. saved before latest
      // draft fields) and normalizeReceivingRecords always provides items: [] so
      // we must not trust the list payload for form hydration.
      loadDraftGenerationRef.current += 1;
      const res = await axios.get(storeApi(`/receiving/${record._id}`));
      const rec = res.data;
      if (!rec) {
        showToast('Receiving record not found', 'error');
        return;
      }
      const poId = rec.purchaseOrderId?._id || rec.purchaseOrderId || '';
      setSelectedPoId(poId ? String(poId) : '');
      setFormData({
        supplier: rec.supplier?._id || rec.supplier || suppliers[0]?._id || '',
        purchaseOrderNumber: rec.purchaseOrderNumber || '',
        discrepancies: rec.discrepancies || ''
      });
      const aircraftLine = (rec.items || []).find(
        (line) => String(line.aircraftScope || '').toUpperCase() === 'AIRCRAFT'
      );
      const aircraftId = resolveReceivingAircraftId(
        aircraftLine?.aircraftId?._id ?? aircraftLine?.aircraftId
      );
      setSelectedReceivingAircraft(aircraftId || 'GEN');
      if (String(rec.loanDirection || '').toUpperCase() === 'LOAN_IN') {
        const purpose = String(rec.loanReceivingPurpose || 'LOAN_IN').trim().toUpperCase();
        const isReturn = purpose === 'LOAN_IN_RETURN' || purpose === 'LOAN_OUT_RETURN';
        setLoanReceivingMeta({
          counterpartyCompany: rec.counterpartyCompany || '',
          counterpartyContactName: rec.counterpartyContactName || '',
          counterpartyContactPhone: rec.counterpartyContactPhone || '',
          loanReferenceNumber: rec.loanReferenceNumber || '',
          returnDueDate: rec.returnDueDate ? shelfToInputValue(rec.returnDueDate) : '',
          loanReceivingPurpose: purpose,
          linkedSourceRecordId: rec.linkedSourceRecordId
            ? String(rec.linkedSourceRecordId._id ?? rec.linkedSourceRecordId)
            : '',
          lockMeta: isReturn
        });
      } else {
        setLoanReceivingMeta({
          counterpartyCompany: '',
          counterpartyContactName: '',
          counterpartyContactPhone: '',
          loanReferenceNumber: '',
          returnDueDate: '',
          loanReceivingPurpose: 'LOAN_IN',
          linkedSourceRecordId: '',
          lockMeta: false
        });
      }
      const mappedItems = await hydrateReceivingItemsForForm(rec.items, poId);
      setItems(mappedItems);
      setCertificateFilesByRow({});
      setFormReceivingDocuments(Array.isArray(rec.documents) ? rec.documents : []);
      if (mode === 'draft' || mode === 'pending') {
        try {
          const docsRes = await axios.get(`/api/store-support/receiving/${rec._id}/documents`);
          setFormReceivingDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
        } catch {
          // keep documents already loaded from the record payload
        }
      }
      if (mode === 'draft') {
        setDraftRecordId(String(rec._id));
        setEditingPendingRecordId('');
        setResubmitOfRecordId(
          rec.revisedFromReceivingId
            ? String(rec.revisedFromReceivingId._id ?? rec.revisedFromReceivingId)
            : ''
        );
        window.localStorage.setItem(autosaveStorageKey, String(rec._id));
      } else if (mode === 'pending') {
        setEditingPendingRecordId(String(rec._id));
        setDraftRecordId('');
        setResubmitOfRecordId('');
        window.localStorage.removeItem(autosaveStorageKey);
      } else {
        setEditingPendingRecordId('');
        setDraftRecordId('');
        setResubmitOfRecordId(mode === 'resubmit' ? String(rec._id) : '');
        window.localStorage.removeItem(autosaveStorageKey);
      }
      setFormEditMode(mode);
      setFormEditRecordRef(recordProcurementReference(rec));
      const modeLabel = mode === 'draft'
        ? 'Draft loaded into the form.'
        : mode === 'pending'
          ? 'Pending submission loaded for editing.'
          : 'Rejected record loaded — revise and submit as a new request.';
      showToast(modeLabel, 'info');
      formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to load receiving record into form'), 'error');
    }
  }, [autosaveStorageKey, showToast, storeApi, suppliers]);

  const validateLoanReturnAtSubmit = useCallback(async () => {
    if (!isLoanReturnReceiving || !loanReceivingMeta.linkedSourceRecordId) return null;
    const purpose = loanReceivingMeta.loanReceivingPurpose;
    try {
      let sourceItems = [];
      if (purpose === 'LOAN_OUT_RETURN') {
        const res = await axios.get(`/api/store/loan/issuances/${loanReceivingMeta.linkedSourceRecordId}`);
        sourceItems = Array.isArray(res.data?.items) ? res.data.items : [];
      } else {
        const res = await axios.get(storeApi(`/receiving/${loanReceivingMeta.linkedSourceRecordId}`));
        sourceItems = Array.isArray(res.data?.items) ? res.data.items : [];
      }
      return validateLoanReturnItemsForSubmit(items, sourceItems, purpose);
    } catch (error) {
      return getErrorMessage(error, 'Failed to validate return quantities against the linked loan record.');
    }
  }, [
    isLoanReturnReceiving,
    items,
    loanReceivingMeta.linkedSourceRecordId,
    loanReceivingMeta.loanReceivingPurpose,
    storeApi
  ]);

  useEffect(() => {
    const init = location.state?.receivingWorkflowFilter;
    if (init === 'PENDING') {
      setWorkflowFilter('PENDING');
    }
  }, [location.state?.receivingWorkflowFilter]);

  const mergeLoanMeta = useCallback((patch) => {
    setLoanReceivingMeta((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleLoanProfileApply = useCallback((profile, record) => {
    if (loanReceivingMeta.lockMeta) return;

    const purpose = String(loanReceivingMeta.loanReceivingPurpose || 'LOAN_IN').trim().toUpperCase();
    const isReturnFlow = purpose === 'LOAN_IN_RETURN' || purpose === 'LOAN_OUT_RETURN';
    let nextPurpose = purpose;
    let linkedSourceRecordId = isReturnFlow ? (loanReceivingMeta.linkedSourceRecordId || profile.recordId) : '';

    let receivingItems = [];
    if (isReturnFlow) {
      receivingItems = buildReceivingItemsForLoanRecord(record, profile.type, nextPurpose);
    } else if (purpose === 'LOAN_IN' && profile.type === 'LOAN_IN') {
      receivingItems = buildReceivingItemsForLoanRecord(record, profile.type, 'LOAN_IN');
    }

    if (receivingItems.length > 0) {
      setItems(receivingItems);
      setCertificateFilesByRow({});
      const aircraftLine = receivingItems.find(
        (line) => String(line.aircraftScope || '').toUpperCase() === 'AIRCRAFT' && line.aircraftId
      );
      if (aircraftLine?.aircraftId) {
        setSelectedReceivingAircraft(String(aircraftLine.aircraftId?._id ?? aircraftLine.aircraftId));
      }
    }

    mergeLoanMeta({
      loanReceivingPurpose: nextPurpose,
      linkedSourceRecordId: isReturnFlow ? linkedSourceRecordId : ''
    });

    if (profile.supplier) {
      setFormData((prev) => ({ ...prev, supplier: profile.supplier }));
    }
  }, [loanReceivingMeta.lockMeta, loanReceivingMeta.loanReceivingPurpose, loanReceivingMeta.linkedSourceRecordId, mergeLoanMeta]);

  useEffect(() => {
    if (!isLoanRentalContext || !initialLoanPrefill) return;
    const prefill = initialLoanPrefill;
    mergeLoanMeta({
      counterpartyCompany: prefill.counterpartyCompany || '',
      counterpartyContactName: prefill.counterpartyContactName || '',
      counterpartyContactPhone: prefill.counterpartyContactPhone || '',
      loanReferenceNumber: prefill.loanReferenceNumber || '',
      returnDueDate: prefill.returnDueDate || '',
      loanReceivingPurpose: prefill.loanReceivingPurpose || 'LOAN_IN',
      linkedSourceRecordId: prefill.linkedSourceRecordId || '',
      lockMeta: Boolean(prefill.lockMeta)
    });
    if (prefill.supplier) {
      setFormData((prev) => ({ ...prev, supplier: prefill.supplier }));
    }
    if (Array.isArray(prefill.items) && prefill.items.length > 0) {
      setItems(prefill.items);
      const aircraftLine = prefill.items.find(
        (line) => String(line.aircraftScope || '').toUpperCase() === 'AIRCRAFT' && line.aircraftId
      );
      if (aircraftLine?.aircraftId) {
        setSelectedReceivingAircraft(String(aircraftLine.aircraftId?._id ?? aircraftLine.aircraftId));
      }
    }
  }, [isLoanRentalContext, initialLoanPrefill, mergeLoanMeta]);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem('receivingHistoryArchivedFields', JSON.stringify(archivedHistoryFields));
    } catch {
      // ignore persistence errors
    }
  }, [archivedHistoryFields]);

  const fetchReceivingHistory = useCallback(async () => {
    try {
      setLoadingReceivingHistory(true);
      const params = {};
      if (workflowFilter === 'PENDING') params.workflowStatus = 'PENDING_INSPECTOR_APPROVAL';
      else if (workflowFilter === 'REJECTED') params.workflowStatus = 'REJECTED';
      else if (workflowFilter === 'APPROVED') params.workflowStatus = 'APPROVED';
      else if (workflowFilter === 'DRAFT') params.workflowStatus = 'DRAFT';
      if (receivingContext) params.receivingCategory = receivingContext;
      const response = await axios.get(storeApi('/receiving'), { params });
      setReceivingHistory(normalizeReceivingRecords(response.data));
    } catch (error) {
      console.error('Error fetching receiving history:', error);
      showToast(getErrorMessage(error, 'Error loading receiving history'), 'error');
    } finally {
      setLoadingReceivingHistory(false);
    }
  }, [showToast, workflowFilter, storeApi, receivingContext]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    const loadAircraft = async () => {
      try {
        const res = await axios.get('/api/aircraft');
        const list = Array.isArray(res.data) ? res.data : res.data?.aircraft || res.data?.data || [];
        setAircraftList(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Error fetching aircraft for receiving filters', e);
      }
    };
    loadAircraft();
  }, []);

  useEffect(() => {
    fetchReceivingHistory();
  }, [fetchReceivingHistory]);

  useEffect(() => {
    if (recordType === 'receiving' && linkRecordId && receivingHistory.length > 0) {
      const exists = receivingHistory.some((r) => String(r._id) === String(linkRecordId));
      if (exists) setExpandedRows((prev) => ({ ...prev, [linkRecordId]: true }));
    }
  }, [recordType, linkRecordId, receivingHistory]);

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      const [issued, partial] = await Promise.all([
        axios.get('/api/store/purchase-orders', { params: { status: 'ISSUED' } }),
        axios.get('/api/store/purchase-orders', { params: { status: 'PARTIALLY_RECEIVED' } })
      ]);
      const issuedRows = Array.isArray(issued.data) ? issued.data : [];
      const partialRows = Array.isArray(partial.data) ? partial.data : [];
      setPurchaseOrders([...issuedRows, ...partialRows]);
    } catch (e) {
      console.error('Error fetching purchase orders', e);
    }
  }, []);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get('/api/master-data/agencies', {
        params: { isActive: 'true', type: 'STORE_SUPPLIER' }
      });
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  useEffect(() => {
    if (suppliers.length > 0) {
      setFormData((prev) => {
        if (!prev.supplier) {
          return { ...prev, supplier: suppliers[0]._id };
        }
        return prev;
      });
    }
  }, [suppliers]);

  useEffect(() => {
    const loadDraft = async () => {
      const generation = ++loadDraftGenerationRef.current;
      if (initialLoanPrefill?.lockMeta) {
        skipAutosaveRef.current = false;
        return;
      }
      const storedId = window.localStorage.getItem(autosaveStorageKey);
      if (!storedId) {
        skipAutosaveRef.current = false;
        return;
      }
      try {
        const response = await axios.get(storeApi(`/receiving/${storedId}`));
        if (generation !== loadDraftGenerationRef.current) return;
        const draft = response.data;
        if (!draft || draft.workflowStatus !== 'DRAFT') {
          window.localStorage.removeItem(autosaveStorageKey);
          skipAutosaveRef.current = false;
          return;
        }
        setDraftRecordId(draft._id);
        const draftPoId = draft.purchaseOrderId?._id || draft.purchaseOrderId || '';
        setSelectedPoId(draftPoId ? String(draftPoId) : '');
        const aircraftLine = draft.items?.find(
          (line) => String(line.aircraftScope || '').toUpperCase() === 'AIRCRAFT'
        );
        setSelectedReceivingAircraft(
          resolveReceivingAircraftId(aircraftLine?.aircraftId?._id ?? aircraftLine?.aircraftId) || 'GEN'
        );
        setFormData((prev) => ({
          ...prev,
          supplier: draft.supplier?._id || draft.supplier || prev.supplier || '',
          purchaseOrderNumber: draft.purchaseOrderNumber || '',
          discrepancies: draft.discrepancies || ''
        }));
        if (String(draft.loanDirection || '').toUpperCase() === 'LOAN_IN') {
          setLoanReceivingMeta({
            counterpartyCompany: draft.counterpartyCompany || '',
            counterpartyContactName: draft.counterpartyContactName || '',
            counterpartyContactPhone: draft.counterpartyContactPhone || '',
            loanReferenceNumber: draft.loanReferenceNumber || '',
            returnDueDate: draft.returnDueDate ? shelfToInputValue(draft.returnDueDate) : '',
            loanReceivingPurpose: draft.loanReceivingPurpose || 'LOAN_IN',
            linkedSourceRecordId: draft.linkedSourceRecordId
              ? String(draft.linkedSourceRecordId._id ?? draft.linkedSourceRecordId)
              : '',
            lockMeta: false
          });
        }
        const loadedItems = await hydrateReceivingItemsForForm(draft.items, draftPoId);
        if (generation !== loadDraftGenerationRef.current) return;
        setItems(loadedItems);
        setCertificateFilesByRow({});
        setFormReceivingDocuments(Array.isArray(draft.documents) ? draft.documents : []);
        try {
          const docsRes = await axios.get(`/api/store-support/receiving/${draft._id}/documents`);
          if (generation !== loadDraftGenerationRef.current) return;
          setFormReceivingDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
        } catch {
          // keep documents already loaded from the record payload
        }
        setFormEditMode('draft');
        setFormEditRecordRef(
          recordProcurementReference(draft) || `Draft ${String(draft._id).slice(-6)}`
        );
      } catch (error) {
        if (generation === loadDraftGenerationRef.current) {
          window.localStorage.removeItem(autosaveStorageKey);
        }
      } finally {
        if (generation === loadDraftGenerationRef.current) {
          skipAutosaveRef.current = false;
        }
      }
    };
    loadDraft();
  }, [autosaveStorageKey, storeApi, initialLoanPrefill?.lockMeta]);

  const receivingHeaderAircraft = useMemo(() => {
    if (selectedReceivingAircraft === 'GEN') {
      return { scope: 'GEN', id: '', registration: 'GEN' };
    }
    const ac = aircraftList.find((a) => String(a._id) === String(selectedReceivingAircraft));
    return {
      scope: 'AIRCRAFT',
      id: String(selectedReceivingAircraft),
      registration: ac?.registration || ''
    };
  }, [selectedReceivingAircraft, aircraftList]);

  const receivingAircraftOptions = useMemo(() => {
    const list = Array.isArray(aircraftList) ? aircraftList : [];
    if (isFromAircraftContext) return list;
    return [{ _id: 'GEN', registration: 'GEN (common components)', __gen: true }, ...list];
  }, [aircraftList, isFromAircraftContext]);

  const selectedReceivingAircraftOption = useMemo(() => {
    if (!selectedReceivingAircraft) return null;
    return (
      receivingAircraftOptions.find((a) => String(a._id) === String(selectedReceivingAircraft))
      || null
    );
  }, [receivingAircraftOptions, selectedReceivingAircraft]);

  const formatReceivingAircraftOptionLabel = useCallback((opt) => {
    if (!opt) return '';
    if (opt.__gen || String(opt._id) === 'GEN') return 'GEN (common components)';
    const reg = String(opt.registration || '').trim();
    const model = String(opt.model || opt.modelVariant || '').trim();
    if (reg && model) return `${reg} — ${model}`;
    return reg || String(opt._id || '');
  }, []);

  const buildReceivingPayload = useCallback(({ withoutPo = false } = {}) => {
    const payloadItems = items.map(({ alreadyReceived, ...rest }) => {
      const unitDetails = Array.isArray(rest.unitDetails)
        ? rest.unitDetails.map((unit) => {
            const unitExpiry = unitExpiryForPersistence(unit?.expiryDate);
            return {
              condition: partConditionSelectValue(unit?.condition),
              batchNumber: unit?.batchNumber || undefined,
              serialNumber: unit?.serialNumber || undefined,
              storeType: normalizeStoreTypeValue(unit?.storeType) || normalizeStoreTypeValue(rest.storeType) || undefined,
              expiryDate: unitExpiry
                ? new Date(unitExpiry).toISOString()
                : (!rest.shelfLifeNotApplicable && rest.shelfLifeUpto
                  ? new Date(rest.shelfLifeUpto).toISOString()
                  : undefined)
            };
          })
        : [];
      const hasDatedUnitExpiry = unitDetails.some((unit) => Boolean(unit.expiryDate));
      const shelfLifeNotApplicable = Boolean(rest.shelfLifeNotApplicable) && !hasDatedUnitExpiry;
      const receivedQty = Math.max(0, Number(rest.receivedQuantity) || 0);
      const quantity = rest.poLineId
        ? Math.max(0.001, Number(rest.quantity) || 1)
        : Math.max(0.001, receivedQty, Number(rest.quantity) || 0);
      const lineAircraftScope = rest.aircraftScope || (selectedReceivingAircraft === 'GEN' ? 'GEN' : 'AIRCRAFT');
      const lineAircraftId = rest.aircraftId || (selectedReceivingAircraft === 'GEN' ? undefined : selectedReceivingAircraft);
      const lineAircraft = aircraftList.find((a) => String(a._id) === String(lineAircraftId));
      return {
        ...rest,
        quantity,
        receivedQuantity: receivedQty,
        condition: deriveReceivingLineCondition({ ...rest, unitDetails }),
        poLineId: rest.poLineId || undefined,
        poLineNumber: rest.poLineNumber != null && Number.isFinite(Number(rest.poLineNumber))
          ? Number(rest.poLineNumber)
          : undefined,
        sourceLineIndex:
          isLoanReturnReceiving && rest.sourceLineIndex != null
            ? Number(rest.sourceLineIndex)
            : undefined,
        unitDetails,
        category: normalizeInventoryCategory(rest.category),
        storePartCode: String(rest.storePartCode || '').trim() || undefined,
        storeType: normalizeStoreTypeValue(rest.storeType) || undefined,
        locationText: String(rest.locationText || '').trim() || undefined,
        location: normalizeReceivingLocation(rest.location),
        reorderPoint: Number(rest.reorderPoint) > 0 ? Number(rest.reorderPoint) : undefined,
        minimumStock: Number(rest.minimumStock) > 0 ? Number(rest.minimumStock) : undefined,
        certificateNumber: rest.certificateNumber || undefined,
        batchNumber: deriveLineBatchNumber(rest, unitDetails) || undefined,
        serialNumber: resolveLineSerialNumber({ ...rest, unitDetails }) || undefined,
        unit: rest.unit || undefined,
        shelfLifeNotApplicable,
        shelfLifeUpto: shelfLifeNotApplicable
          ? undefined
          : (rest.shelfLifeUpto ? new Date(rest.shelfLifeUpto).toISOString() : undefined),
        aircraftScope: lineAircraftScope,
        aircraftId: lineAircraftScope === 'AIRCRAFT' ? lineAircraftId : undefined,
        aircraftRegistration: lineAircraftScope === 'GEN'
          ? 'GEN'
          : (rest.aircraftRegistration || lineAircraft?.registration || '').toUpperCase()
      };
    });
    const payload = {
      supplier: formData.supplier,
      items: payloadItems,
      discrepancies: formData.discrepancies
    };
    if (isLoanRentalContext) {
      const loanPurpose = loanReceivingMeta.loanReceivingPurpose || 'LOAN_IN';
      payload.loanDirection = 'LOAN_IN';
      payload.loanReceivingPurpose = loanPurpose;
      if (isLoanReturnReceiving) {
        payload.linkedSourceRecordId = loanReceivingMeta.linkedSourceRecordId || undefined;
      }
      payload.counterpartyCompany = String(loanReceivingMeta.counterpartyCompany || '').trim();
      payload.counterpartyContactName = String(loanReceivingMeta.counterpartyContactName || '').trim() || undefined;
      payload.counterpartyContactPhone = String(loanReceivingMeta.counterpartyContactPhone || '').trim() || undefined;
      payload.loanReferenceNumber = String(loanReceivingMeta.loanReferenceNumber || '').trim() || undefined;
      payload.returnDueDate = loanReceivingMeta.returnDueDate
        ? new Date(loanReceivingMeta.returnDueDate).toISOString()
        : undefined;
    }
    const manualPoNumber = String(formData.purchaseOrderNumber || '').trim();
    const attachPo = !contextHidesPurchaseOrder && !withoutPo;
    if (attachPo && selectedPoId) {
      payload.purchaseOrderId = selectedPoId;
      payload.purchaseOrderNumber = manualPoNumber
        || purchaseOrders.find((p) => String(p._id) === String(selectedPoId))?.poNumber;
    } else if (attachPo && manualPoNumber) {
      payload.purchaseOrderNumber = manualPoNumber;
    }
    if (receivingContext) {
      payload.receivingCategory = receivingContext;
    }
    return payload;
  }, [
    aircraftList,
    contextHidesPurchaseOrder,
    formData,
    isLoanRentalContext,
    isLoanReturnReceiving,
    items,
    loanReceivingMeta,
    purchaseOrders,
    receivingContext,
    selectedPoId,
    selectedReceivingAircraft
  ]);

  const uploadReceivingCertificates = async (recordId, filesByRow = certificateFilesByRow) => {
    if (!recordId) return { uploaded: 0, failed: 0 };
    const uploadTasks = Object.entries(filesByRow || {});
    let uploaded = 0;
    let failed = 0;
    for (const [rowIndex, pendingList] of uploadTasks) {
      const pendingFiles = Array.isArray(pendingList) ? pendingList : (pendingList ? [pendingList] : []);
      if (!pendingFiles.length) continue;
      const item = items[parseInt(rowIndex, 10)];
      for (const pending of pendingFiles) {
        const file = pending?.file || pending;
        if (!file) continue;
        const uploadData = new FormData();
        uploadData.append('documents', file);
        uploadData.append(
          'category',
          buildReceivingDocumentCategory(pending?.docType || 'COC', item?.partNumber)
        );
        try {
          await axios.post(
            `/api/store-support/receiving/${recordId}/documents`,
            uploadData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          uploaded += 1;
        } catch (uploadError) {
          console.error('Error uploading certificate:', uploadError);
          failed += 1;
          showToast(
            `Document upload failed${item?.partNumber ? ` for ${item.partNumber}` : ''}.`,
            'warning'
          );
        }
      }
    }
    return { uploaded, failed };
  };

  const refreshFormReceivingDocuments = async (recordId) => {
    if (!recordId) {
      setFormReceivingDocuments([]);
      return;
    }
    try {
      const docsRes = await axios.get(`/api/store-support/receiving/${recordId}/documents`);
      setFormReceivingDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
    } catch (error) {
      console.error('Error loading receiving documents:', error);
    }
  };

  const handleSaveDraft = async () => {
    if (!canSubmitReceiving) {
      showToast('You do not have permission to save receiving drafts', 'error');
      return;
    }
    const hasContent =
      items.length > 0
      || Boolean(selectedPoId)
      || String(formData.purchaseOrderNumber || '').trim()
      || String(formData.discrepancies || '').trim()
      || Object.keys(certificateFilesByRow).length > 0
      || (isLoanRentalContext
        && (String(loanReceivingMeta.counterpartyCompany || '').trim()
          || String(loanReceivingMeta.loanReferenceNumber || '').trim()));
    if (!hasContent) {
      showToast('Nothing to save. Add items or details before saving a draft.', 'info');
      return;
    }
    try {
      setAutosaveState((prev) => ({ ...prev, saving: true, error: '' }));
      const payload = buildReceivingPayload({ withoutPo: false });
      if (resubmitOfRecordId) payload.resubmitOf = resubmitOfRecordId;
      let response;
      if (draftRecordId) {
        response = await axios.put(storeApi(`/receive/${draftRecordId}/draft`), payload);
      } else {
        response = await axios.post(storeApi('/receive/draft'), payload);
        if (response.data?._id) {
          setDraftRecordId(response.data._id);
          window.localStorage.setItem(autosaveStorageKey, response.data._id);
        }
      }
      const savedId = response?.data?._id ? String(response.data._id) : '';
      if (savedId && !draftRecordId) {
        setDraftRecordId(savedId);
      }
      if (savedId) {
        setEditingPendingRecordId('');
        setFormEditMode('draft');
        setFormEditRecordRef(
          recordProcurementReference(response.data) || `Draft ${savedId.slice(-6)}`
        );
        window.localStorage.setItem(autosaveStorageKey, savedId);

        // Keep history row in sync with the just-saved payload so Continue
        // draft / expand never shows pre-save empty lines while refresh runs.
        setReceivingHistory((prev) => {
          const next = normalizeReceivingRecords(
            Array.isArray(prev) ? prev : []
          );
          const idx = next.findIndex((r) => String(r._id) === savedId);
          const saved = { ...response.data, items: Array.isArray(response.data?.items) ? response.data.items : [] };
          if (idx >= 0) {
            const copy = [...next];
            copy[idx] = { ...copy[idx], ...saved };
            return copy;
          }
          return [saved, ...next];
        });

        const pendingUploads = Object.keys(certificateFilesByRow).length > 0;
        if (pendingUploads) {
          const { uploaded } = await uploadReceivingCertificates(savedId);
          if (uploaded > 0) {
            setCertificateFilesByRow({});
          }
        }
        await refreshFormReceivingDocuments(savedId);
      }
      await fetchReceivingHistory();
      setAutosaveState({ saving: false, lastSavedAt: new Date(), error: '' });
      showToast('Draft saved successfully.', 'success');
    } catch (error) {
      setAutosaveState((prev) => ({
        ...prev,
        saving: false,
        error: getErrorMessage(error, 'Failed to save draft')
      }));
      showToast(getErrorMessage(error, 'Failed to save draft'), 'error');
    }
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
    setCertificateFilesByRow((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const key = parseInt(k, 10);
        if (Number.isNaN(key) || key === index) return;
        next[key > index ? key - 1 : key] = prev[k];
      });
      return next;
    });
  };

  const handleCertificateFileChange = (index, files, docType = 'COC') => {
    const incoming = Array.from(files || []).filter(Boolean);
    if (!incoming.length) return;
    setCertificateFilesByRow((prev) => {
      const existing = Array.isArray(prev[index])
        ? prev[index]
        : (prev[index] ? [{ file: prev[index], docType: 'COC' }] : []);
      return {
        ...prev,
        [index]: [
          ...existing,
          ...incoming.map((file) => ({ file, docType: docType || 'COC' }))
        ]
      };
    });
  };

  const handleCertificatePendingRemove = (index, pendingIndex) => {
    setCertificateFilesByRow((prev) => {
      const existing = Array.isArray(prev[index])
        ? prev[index]
        : (prev[index] ? [{ file: prev[index], docType: 'COC' }] : []);
      const nextList = existing.filter((_, i) => i !== pendingIndex);
      const next = { ...prev };
      if (nextList.length) next[index] = nextList;
      else delete next[index];
      return next;
    });
  };

  const activeFormRecordId = editingPendingRecordId || draftRecordId;

  const certificateServerDocsByRow = useMemo(() => {
    if (!activeFormRecordId || !formReceivingDocuments.length) return {};
    const byRow = {};
    items.forEach((item, index) => {
      const docs = findReceivingCertificateDocuments(formReceivingDocuments, item.partNumber);
      if (docs.length) {
        byRow[index] = { recordId: activeFormRecordId, docs };
      }
    });
    return byRow;
  }, [items, formReceivingDocuments, activeFormRecordId]);

  const handleCertificateServerDownload = async (rowIndex, { recordId, doc }) => {
    if (!recordId || !doc?._id || downloadingCertificateRowKey) return;
    const key = `${rowIndex}:${doc._id}`;
    setDownloadingCertificateRowKey(key);
    try {
      await downloadReceivingCertificate(recordId, doc);
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to download certificate'), 'error');
    } finally {
      setDownloadingCertificateRowKey('');
    }
  };

  const handleReceivingHistoryCertificateDownload = async (recordId, doc, rowKey) => {
    if (!recordId || !doc?._id || downloadingCertificateRowKey) return;
    setDownloadingCertificateRowKey(rowKey);
    try {
      await downloadReceivingCertificate(recordId, doc);
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to download certificate'), 'error');
    } finally {
      setDownloadingCertificateRowKey('');
    }
  };

  const openPoDeleteDialog = (poId, poNumber) => {
    if (!poId) return;
    setPoDeleteDialog({
      open: true,
      targets: [{ _id: String(poId), poNumber: poNumber || '' }]
    });
  };

  const handlePoSelect = async (poId) => {
    setSelectedPoId(poId || '');
    if (poId) {
      const poFromList = purchaseOrders.find((p) => String(p._id) === String(poId));
      const orderToId = poFromList?.orderTo?._id || poFromList?.orderTo;
      setFormData((prev) => ({
        ...prev,
        supplier: orderToId || prev.supplier,
        purchaseOrderNumber: poFromList?.poNumber || prev.purchaseOrderNumber
      }));
      try {
        const res = await axios.get(`/api/store/purchase-orders/${poId}`);
        const po = res.data;
        const poAircraftId = po?.aircraftId?._id || po?.aircraftId || 'GEN';
        setSelectedReceivingAircraft(poAircraftId ? String(poAircraftId) : 'GEN');
        setFormData((prev) => ({
          ...prev,
          supplier: po?.orderTo?._id || po?.orderTo || prev.supplier,
          purchaseOrderNumber: po?.poNumber || prev.purchaseOrderNumber
        }));
        const lineItems = po.lineItems || [];
        setItems((prev) => mergePoLinesWithExistingReceivingItems(lineItems, prev));
        setCertificateFilesByRow({});
      } catch (e) {
        console.error('Error loading PO detail', e);
        showToast('Failed to load PO lines', 'error');
        setItems([]);
      }
    } else {
      setSelectedReceivingAircraft('GEN');
      setFormData((prev) => ({ ...prev, purchaseOrderNumber: '' }));
      setItems([]);
      setCertificateFilesByRow({});
    }
  };

  const handlePoDeleted = async () => {
    const deletedTargets = poDeleteDialog.targets || [];
    const deletedIds = new Set(deletedTargets.map((po) => String(po._id)));
    setPoDeleteDialog({ open: false, targets: [] });
    if (deletedIds.has(String(selectedPoId))) {
      await handlePoSelect('');
    }
    await fetchPurchaseOrders();
    await fetchReceivingHistory();
    setReceivingHistory((prev) => prev.map((rec) => {
      const linkedId = rec.purchaseOrderId?._id || rec.purchaseOrderId;
      if (!linkedId || !deletedIds.has(String(linkedId))) return rec;
      const poMeta = deletedTargets.find((po) => String(po._id) === String(linkedId));
      return {
        ...rec,
        purchaseOrderId: null,
        purchaseOrderNumber: rec.purchaseOrderNumber || poMeta?.poNumber || ''
      };
    }));
  };

  const validateItemsForSubmit = () => {
    const missingPart = items.find((it) => !String(it.partNumber || '').trim());
    if (missingPart) {
      showToast('Part number is required for every line item', 'error');
      return false;
    }
    const missingDesc = items.find((it) => !String(it.description || '').trim());
    if (missingDesc) {
      showToast('Description is required for every line item', 'error');
      return false;
    }
    if (selectedPoId) {
      const overLine = items.find((it) => {
        if (it?.lineStatus === 'CLOSED') return false;
        const receiving = Number(it.receivedQuantity) || 0;
        if (receiving <= 0) return false;
        const ordered = Number(it.quantity) || 0;
        const already = Number(it.alreadyReceived) || 0;
        return receiving > Math.max(0, ordered - already);
      });
      if (overLine) {
        const ordered = Number(overLine.quantity) || 0;
        const already = Number(overLine.alreadyReceived) || 0;
        const remaining = Math.max(0, ordered - already);
        showToast(
          `Receiving qty for ${overLine.partNumber} exceeds remaining (${remaining} left of ${ordered} ordered).`,
          'error'
        );
        return false;
      }
    }
    const totalReceived = items.reduce((sum, it) => sum + (Number(it.receivedQuantity) || 0), 0);
    if (totalReceived <= 0) {
      showToast('At least one line must have received quantity greater than zero', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async ({ withoutPo = false } = {}) => {
    if (items.length === 0) {
      showToast('Please add at least one item', 'error');
      return;
    }
    if (!validateItemsForSubmit()) return;

    if (isLoanRentalContext) {
      if (!String(loanReceivingMeta.counterpartyCompany || '').trim()) {
        showToast('Recipient company is required for loan/rental receiving.', 'error');
        return;
      }
      if (!loanReceivingMeta.returnDueDate) {
        showToast('Return due date is required for loan/rental receiving.', 'error');
        return;
      }
      const hasMappedReturnLines = items.some(
        (line) => Number(line.receivedQuantity) > 0
          && line.sourceLineIndex != null
          && Number.isFinite(Number(line.sourceLineIndex))
      );
      if (hasMappedReturnLines && !isLoanReturnReceiving) {
        showToast(
          'These lines are linked to a loan return. Use Return via Receiving from the Loan page or select the loan reference again.',
          'error'
        );
        return;
      }
      if (isLoanReturnReceiving) {
        if (!loanReceivingMeta.linkedSourceRecordId) {
          showToast('Linked loan record is missing for this return receiving.', 'error');
          return;
        }
        if (!String(formData.discrepancies || '').trim()) {
          showToast('Return reason is required (enter in discrepancies / notes).', 'error');
          return;
        }
        const missingMap = items.filter(
          (line) => Number(line.receivedQuantity) > 0
            && (line.sourceLineIndex == null || !Number.isFinite(Number(line.sourceLineIndex)))
        );
        if (missingMap.length > 0) {
          showToast(
            'Each return line must map to the original loan. Re-open from Loan history → Return via Receiving.',
            'error'
          );
          return;
        }
      }
    }
    if (isFromAircraftContext && selectedReceivingAircraft === 'GEN') {
      showToast('Select a specific aircraft for From Aircraft receiving.', 'error');
      return;
    }
    if (isAgainstPoContext && !withoutPo) {
      const hasPo = Boolean(selectedPoId) || String(formData.purchaseOrderNumber || '').trim();
      if (!hasPo) {
        showToast('Select or enter a purchase order for Against PO receiving.', 'error');
        return;
      }
    }

    if (!canSubmitReceiving) {
      showToast('You do not have permission to submit receiving', 'error');
      return;
    }

    if (isLoanReturnReceiving) {
      const loanReturnError = await validateLoanReturnAtSubmit();
      if (loanReturnError) {
        showToast(loanReturnError, 'error');
        return;
      }
    }

    try {
      setLoading(true);
      const payload = buildReceivingPayload({ withoutPo });
      if (draftRecordId && !editingPendingRecordId) payload.draftId = draftRecordId;
      if (resubmitOfRecordId && !editingPendingRecordId) {
        payload.resubmitOf = resubmitOfRecordId;
      }
      let response;
      if (editingPendingRecordId) {
        response = await axios.put(storeApi(`/receive/${editingPendingRecordId}/pending`), payload);
        showToast('Pending submission updated.', 'success');
      } else {
        response = await axios.post(storeApi('/receive/submit'), payload);
        showToast(
          resubmitOfRecordId
            ? 'Revised receiving submitted for Store Inspector/AM approval.'
            : 'Submitted for Store Inspector/AM approval. Inventory updates after approval.',
          'success'
        );
      }
      if (response.data?._id) {
        const { failed } = await uploadReceivingCertificates(response.data._id);
        if (failed > 0) {
          showToast('Receiving saved, but one or more certificate uploads failed.', 'warning');
        }
      }
      if (selectedPoId) {
        try {
          const poRes = await axios.get(`/api/store/purchase-orders/${selectedPoId}`);
          const po = poRes.data;
          const lineItems = po.lineItems || [];
          setItems(mapPoLinesToReceivingItems(lineItems));
          setCertificateFilesByRow({});
        } catch (e) {
          setItems([]);
          setCertificateFilesByRow({});
        }
      } else {
        setFormData({ supplier: suppliers[0]?._id || '', purchaseOrderNumber: '', discrepancies: '' });
        setItems([]);
        setCertificateFilesByRow({});
        if (isLoanRentalContext) {
          setLoanReceivingMeta({
            counterpartyCompany: '',
            counterpartyContactName: '',
            counterpartyContactPhone: '',
            loanReferenceNumber: '',
            returnDueDate: '',
            loanReceivingPurpose: 'LOAN_IN',
            linkedSourceRecordId: '',
            lockMeta: false
          });
        }
      }
      setDraftRecordId('');
      setEditingPendingRecordId('');
      setFormEditMode(null);
      setFormEditRecordRef('');
      setResubmitOfRecordId('');
      window.localStorage.removeItem(autosaveStorageKey);
      setAutosaveState({ saving: false, lastSavedAt: null, error: '' });
      if (response.data?._id) {
        setDocumentRecordId(response.data._id);
        const supplierMeta =
          suppliers.find((s) => s._id === response.data.supplier) || {};
        setLastReceivingSummary({
          id: response.data._id,
          supplier: supplierMeta.name,
          supplierCode: supplierMeta.code,
          purchaseOrderNumber: response.data.purchaseOrderNumber,
          loanReferenceNumber: response.data.loanReferenceNumber,
          loanDirection: response.data.loanDirection,
          receivedAt: response.data.receivingDate
        });
        setDocumentDialog({
          open: true,
          recordId: response.data._id,
          label:
            response.data.loanDirection === 'LOAN_IN'
              ? (String(response.data.loanReferenceNumber || '').trim()
                ? `Loan ${response.data.loanReferenceNumber}`
                : `Record ${response.data._id.slice(-6)}`)
              : response.data.purchaseOrderNumber
                ? `PO ${response.data.purchaseOrderNumber}`
                : `Record ${response.data._id.slice(-6)}`
        });
        showToast('Receiving record saved. You can attach documents now.', 'info');
        fetchReceivingHistory();
      }
    } catch (error) {
      console.error('Error receiving items:', error);
      showToast(getErrorMessage(error, 'Error receiving items. Please try again.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitWithoutPo = () => {
    if (selectedPoId || String(formData.purchaseOrderNumber || '').trim()) {
      setSelectedPoId('');
      setFormData((prev) => ({ ...prev, purchaseOrderNumber: '' }));
    }
    handleSubmit({ withoutPo: true });
  };

  const deriveApprovalHeaderAircraft = (rec) => {
    const po = rec.purchaseOrderId;
    if (po && typeof po === 'object') {
      const id = po.aircraftId?._id ?? po.aircraftId;
      if (id != null) {
        return {
          scope: 'AIRCRAFT',
          id: String(id),
          registration: po.aircraftRegistration || po.aircraftId?.registration || ''
        };
      }
    }
    const line = (rec.items || []).find(
      (l) => String(l.aircraftScope || '').toUpperCase() === 'AIRCRAFT' && l.aircraftId
    );
    if (line) {
      const id = line.aircraftId?._id ?? line.aircraftId;
      return {
        scope: 'AIRCRAFT',
        id: String(id),
        registration: line.aircraftRegistration || ''
      };
    }
    return { scope: 'GEN', id: '', registration: 'GEN' };
  };

  const openApprovalDialog = async (record) => {
    try {
      setApprovalLoading(true);
      const res = await axios.get(storeApi(`/receiving/${record._id}`));
      const rec = res.data;
      setApprovalDialog({
        open: true,
        recordId: rec._id,
        loanReceivingPurpose:
          rec.loanDirection === 'LOAN_IN'
            ? String(rec.loanReceivingPurpose || 'LOAN_IN').trim().toUpperCase()
            : null,
        hasPurchaseOrder: !!(rec.purchaseOrderId?._id || rec.purchaseOrderId),
        submittedById: rec.submittedBy?._id || rec.submittedBy || rec.receivedBy?._id || rec.receivedBy || null,
        headerAircraft: deriveApprovalHeaderAircraft(rec)
      });
      const approvalPoId = rec.purchaseOrderId?._id || rec.purchaseOrderId || '';
      setApprovalItems(
        await hydrateReceivingItemsForForm(rec.items, approvalPoId)
      );
      setApprovalDiscrepancies(rec.discrepancies || '');
      setApprovalInspectorNotes(rec.inspectorNotes || '');
      setApprovalRejectionNotes('');
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to load receiving record'), 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  const closeApprovalDialog = () => {
    setApprovalDialog({
      open: false,
      recordId: null,
      loanReceivingPurpose: null,
      hasPurchaseOrder: false,
      submittedById: null,
      headerAircraft: { scope: 'GEN', id: '', registration: 'GEN' }
    });
    setApprovalItems([]);
    setApprovalDiscrepancies('');
    setApprovalInspectorNotes('');
    setApprovalRejectionNotes('');
    setApprovalLineFormDialog({ open: false, rowIndex: null });
  };

  const openApprovalLineFormDialog = (index) => {
    setApprovalLineFormDialog({ open: true, rowIndex: index });
  };

  const closeApprovalLineFormDialog = () => {
    setApprovalLineFormDialog({ open: false, rowIndex: null });
  };

  const handleApprovalLineFormReturn = (mergedLine, errorMessage) => {
    if (errorMessage) {
      showToast(errorMessage, 'error');
      return;
    }
    const rowIndex = approvalLineFormDialog.rowIndex;
    if (rowIndex == null || !mergedLine) return;
    setApprovalItems((prev) => prev.map((item, index) => (index === rowIndex ? mergedLine : item)));
    closeApprovalLineFormDialog();
  };

  const handleApprovalItemChange = (index, field, value) => {
    setApprovalItems((prev) => {
      const next = [...prev];
      let row = {
        ...next[index],
        [field]: field === 'serialNumber' ? normalizeLineSerialNumberDisplay(value) : value
      };
      if (field === 'condition') {
        const condition = partConditionSelectValue(value);
        row.condition = condition;
        if (Array.isArray(row.unitDetails) && row.unitDetails.length > 0) {
          row.unitDetails = row.unitDetails.map((unit) => ({ ...unit, condition }));
        }
      }
      if (field === 'receivedQuantity') {
        const nextQty = Math.max(0, parseFloat(value) || 0);
        const unitCount = receivingUnitDetailCount(nextQty);
        const lineCondition = partConditionSelectValue(row.condition);
        const currentRows = Array.isArray(row.unitDetails) ? row.unitDetails : [];
        const lineStore = normalizeStoreTypeValue(row.storeType) || '';
        row.receivedQuantity = nextQty;
        row.unitDetails = Array.from({ length: unitCount }, (_, unitIndex) => (
          currentRows[unitIndex] || {
            condition: lineCondition,
            batchNumber: '',
            serialNumber: '',
            storeType: lineStore
          }
        ));
        row = syncLineSerialBatchIntoUnitDetails(row);
      }
      if (field === 'storeType') {
        row.unitDetails = withLineStoreTypeOnUnits(row.unitDetails, value, { overwrite: true });
      }
      if (field === 'serialNumber' || field === 'batchNumber') {
        row = syncLineSerialBatchIntoUnitDetails(row);
      }
      next[index] = row;
      return next;
    });
  };

  const handleApprovalLocationChange = (index, field, value) => {
    setApprovalItems((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      return {
        ...row,
        location: {
          ...normalizeReceivingLocation(row.location),
          [field]: value
        }
      };
    }));
  };

  const handleApprovalAddItem = () => {
    setApprovalItems((prev) => [
      ...prev,
      { ...createEmptyReceivingItem(), quantity: 1, _reviewAdded: true }
    ]);
  };

  const handleApprovalRemoveItem = (index) => {
    setApprovalItems((prev) => prev.filter((_, i) => i !== index));
  };

  const buildApprovePayload = () => ({
    items: approvalItems.map((raw) => {
      const it = syncLineSerialBatchIntoUnitDetails(raw);
      const unitDetails = Array.isArray(it.unitDetails)
        ? it.unitDetails.map((unit) => {
            const unitExpiry = unitExpiryForPersistence(unit?.expiryDate);
            return {
              condition: partConditionSelectValue(unit?.condition),
              batchNumber: unit?.batchNumber || undefined,
              serialNumber: unit?.serialNumber || undefined,
              storeType: normalizeStoreTypeValue(unit?.storeType) || normalizeStoreTypeValue(it.storeType) || undefined,
              expiryDate: unitExpiry
                ? new Date(unitExpiry).toISOString()
                : (!it.shelfLifeNotApplicable && it.shelfLifeUpto
                  ? new Date(it.shelfLifeUpto).toISOString()
                  : undefined)
            };
          })
        : undefined;
      const hasDatedUnitExpiry = Array.isArray(unitDetails) && unitDetails.some((unit) => Boolean(unit.expiryDate));
      const shelfLifeNotApplicable = Boolean(it.shelfLifeNotApplicable) && !hasDatedUnitExpiry;
      return {
        partNumber: String(it.partNumber || '').trim(),
        referencePartNumber: String(it.referencePartNumber || '').trim() || undefined,
        description: it.description,
        category: normalizeInventoryCategory(it.category),
        quantity: Math.max(0.001, parseFloat(it.quantity) || 1),
        receivedQuantity: Math.max(0, parseFloat(it.receivedQuantity) || 0),
        poLineId: it.poLineId || undefined,
        poLineNumber: it.poLineNumber != null && Number.isFinite(Number(it.poLineNumber))
          ? Number(it.poLineNumber)
          : undefined,
        sourceLineIndex: it.sourceLineIndex != null && Number.isFinite(Number(it.sourceLineIndex))
          ? Number(it.sourceLineIndex)
          : undefined,
        condition: deriveReceivingLineCondition({ ...it, unitDetails: unitDetails || [] }),
        storeType: normalizeStoreTypeValue(it.storeType) || undefined,
        storePartCode: String(it.storePartCode || '').trim() || undefined,
        locationText: String(it.locationText || '').trim() || undefined,
        location: normalizeReceivingLocation(it.location),
        reorderPoint: Number(it.reorderPoint) > 0 ? Number(it.reorderPoint) : undefined,
        minimumStock: Number(it.minimumStock) > 0 ? Number(it.minimumStock) : undefined,
        certificateNumber: it.certificateNumber || undefined,
        batchNumber: deriveLineBatchNumber(it, unitDetails) || undefined,
        serialNumber: resolveLineSerialNumber({
          ...it,
          unitDetails: unitDetails || it.unitDetails
        }) || undefined,
        unit: it.unit || undefined,
        unitDetails,
        remarks: it.remarks || undefined,
        shelfLifeNotApplicable,
        shelfLifeUpto: shelfLifeNotApplicable
          ? undefined
          : (it.shelfLifeUpto ? new Date(it.shelfLifeUpto).toISOString() : undefined),
        aircraftScope: it.aircraftScope || 'GEN',
        aircraftId: it.aircraftScope === 'AIRCRAFT' ? (it.aircraftId || undefined) : undefined,
        aircraftRegistration: it.aircraftRegistration || (it.aircraftScope === 'GEN' ? 'GEN' : undefined)
      };
    }),
    discrepancies: approvalDiscrepancies,
    inspectorNotes: approvalInspectorNotes
  });

  const collectLineSerialsForPriTag = (item) => {
    const seen = new Set();
    const serials = [];
    const pushSerial = (raw) => {
      String(raw || '')
        .split(',')
        .map((part) => part.trim())
        .filter((serial) => serial && !isPlaceholderSerialNumber(serial))
        .forEach((serial) => {
          if (seen.has(serial)) return;
          seen.add(serial);
          serials.push(serial);
        });
    };
    (Array.isArray(item?.unitDetails) ? item.unitDetails : []).forEach((unit) => pushSerial(unit?.serialNumber));
    (Array.isArray(item?.serialNumbers) ? item.serialNumbers : []).forEach((serial) => pushSerial(serial));
    pushSerial(item?.serialNumber);
    return serials;
  };

  const collectLinePriNumbersForTag = (item) => {
    const pris = [];
    const seen = new Set();
    const push = (raw) => {
      const pri = String(raw || '').trim();
      if (!pri || seen.has(pri)) return;
      seen.add(pri);
      pris.push(pri);
    };
    (Array.isArray(item?.unitDetails) ? item.unitDetails : []).forEach((unit) => push(unit?.priNumber));
    push(item?.priNumber);
    return pris;
  };

  const formatLinePriDisplay = (item) => {
    const pris = collectLinePriNumbersForTag(item);
    return pris.length > 0 ? pris.join(', ') : '—';
  };

  const handleDownloadPriTagPdf = async (recordId, lineIndex, item, { silent = false } = {}) => {
    const pris = collectLinePriNumbersForTag(item);
    if (!recordId || lineIndex == null || pris.length === 0) {
      if (!silent) showToast('PRI tag is not available for this line', 'warning');
      return false;
    }
    const key = `${recordId}:${lineIndex}`;
    if (downloadingPriKey === key) return false;
    setDownloadingPriKey(key);
    try {
      const serials = collectLineSerialsForPriTag(item);
      // Serialized: one PDF per serial (shared line PRI). Batch-only multi-PRI: one PDF per batch PRI.
      const jobs = [];
      if (serials.length > 0) {
        const priNumber = pris[0];
        serials.forEach((serial) => jobs.push({ priNumber, serial }));
      } else {
        pris.forEach((priNumber) => jobs.push({ priNumber, serial: '' }));
      }
      for (const { priNumber, serial } of jobs) {
        const params = new URLSearchParams({ download: '1' });
        params.set('priNumber', priNumber);
        if (serial) params.set('serialNumber', serial);
        const safePri = priNumber.replace(/[^a-zA-Z0-9._-]+/g, '_');
        const safePart = String(item?.partNumber || 'part').replace(/[^a-zA-Z0-9._-]+/g, '_');
        const safeSerial = serial ? String(serial).replace(/[^a-zA-Z0-9._-]+/g, '_') : '';
        const filename = ['PRI-Tag', safePri, safePart, safeSerial].filter(Boolean).join('_') + '.pdf';
        await downloadAuthenticatedPdf(
          `${storeApi(`/receiving/${recordId}/items/${lineIndex}/pri-tag.pdf`)}?${params.toString()}`,
          filename
        );
      }
      if (!silent) {
        showToast(
          jobs.length > 1 ? `${jobs.length} PRI tag PDFs downloaded` : 'PRI tag PDF downloaded',
          'success'
        );
      }
      return true;
    } catch (err) {
      console.error(err);
      showToast(err.message || getErrorMessage(err, 'Failed to download PRI tag PDF'), 'error');
      return false;
    } finally {
      setDownloadingPriKey('');
    }
  };

  const handleApprovalApprove = async () => {
    if (!approvalDialog.recordId) return;
    if (approvalItems.length === 0) {
      showToast('Add at least one part line before approving', 'error');
      return;
    }
    const missingPart = approvalItems.some((it) => !String(it.partNumber || '').trim());
    if (missingPart) {
      showToast('Each line must have a part number', 'error');
      return;
    }
    const total = approvalItems.reduce((s, it) => s + (parseFloat(it.receivedQuantity) || 0), 0);
    if (total <= 0) {
      showToast('At least one line must have received quantity greater than zero', 'error');
      return;
    }
    try {
      setApprovalLoading(true);
      const res = await axios.post(storeApi(`/receive/${approvalDialog.recordId}/approve`), buildApprovePayload());
      const approved = res.data;
      showToast('Receiving approved and applied to inventory', 'success');
      closeApprovalDialog();
      fetchReceivingHistory();
      const tagLines = (approved?.items || []).filter(
        (line) => Number(line.receivedQuantity) > 0 && (
          String(line.priNumber || '').trim()
          || (Array.isArray(line.unitDetails) && line.unitDetails.some((u) => String(u?.priNumber || '').trim()))
        )
      );
      if (approved?._id && tagLines.length > 0) {
        setPriTagDialog({ open: true, record: approved });
      }
      if (selectedPoId) {
        try {
          const poRes = await axios.get(`/api/store/purchase-orders/${selectedPoId}`);
          const po = poRes.data;
          const lineItems = po.lineItems || [];
          setItems(mapPoLinesToReceivingItems(lineItems));
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      showToast(getErrorMessage(e, 'Approval failed'), 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalReject = async () => {
    if (!approvalDialog.recordId) return;
    try {
      setApprovalLoading(true);
      await axios.post(storeApi(`/receive/${approvalDialog.recordId}/reject`), {
        rejectionNotes: approvalRejectionNotes
      });
      showToast('Receiving submission rejected', 'success');
      closeApprovalDialog();
      fetchReceivingHistory();
    } catch (e) {
      showToast(getErrorMessage(e, 'Reject failed'), 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleOpenDocuments = () => {
    if (!documentRecordId) {
      showToast('Enter a receiving record ID to manage documents', 'error');
      return;
    }
    setDocumentDialog({
      open: true,
      recordId: documentRecordId,
      label: `Record ${documentRecordId}`
    });
  };

  const handleDocumentsClose = () => {
    setDocumentDialog({ open: false, recordId: '', label: '' });
  };

  const toggleRowExpansion = (recordId) => {
    setExpandedRows((prev) => ({ ...prev, [recordId]: !prev[recordId] }));
  };

  const setPoDocBusy = (poId, category, busy) => {
    setPoDocBusyByCategory((prev) => ({ ...prev, [`${poId}:${category}`]: busy }));
  };

  const handleDownloadAttachment = async (downloadUrl, filename, docKey) => {
    if (!downloadUrl || downloadingDocKey) return;
    const key = docKey || downloadUrl;
    setDownloadingDocKey(key);
    try {
      const response = await axios.get(downloadUrl, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to download document.'), 'error');
    } finally {
      setDownloadingDocKey(null);
    }
  };

  const fetchPoDocuments = useCallback(async (poId) => {
    if (!poId) return;
    try {
      const res = await axios.get(`/api/store/purchase-orders/${poId}/documents`);
      setPoDocsByPoId((prev) => ({ ...prev, [poId]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      setPoDocsByPoId((prev) => ({ ...prev, [poId]: [] }));
    }
  }, []);

  const getLatestPoDocByCategory = (poId, category) =>
    (Array.isArray(poDocsByPoId[poId]) ? poDocsByPoId[poId] : [])
      .filter((doc) => doc.category === category)
      .sort((a, b) => new Date(b.uploadedAt || 0).getTime() - new Date(a.uploadedAt || 0).getTime())[0];
  const getPoDocsByCategory = (poId, category) =>
    (Array.isArray(poDocsByPoId[poId]) ? poDocsByPoId[poId] : [])
      .filter((doc) => doc.category === category)
      .sort((a, b) => new Date(b.uploadedAt || 0).getTime() - new Date(a.uploadedAt || 0).getTime());

  const handlePoRefDocUpload = async (poId, category, file) => {
    if (!poId || !file) return;
    setPoDocBusy(poId, category, true);
    try {
      const formData = new FormData();
      formData.append('documents', file);
      formData.append('category', category);
      await axios.post(`/api/store/purchase-orders/${poId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Document uploaded', 'success');
      fetchPoDocuments(poId);
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to upload document.'), 'error');
    } finally {
      setPoDocBusy(poId, category, false);
    }
  };

  const handlePoRefDocDelete = async (poId, category, docId) => {
    if (!poId || !docId) return;
    setPoDocBusy(poId, category, true);
    try {
      await axios.delete(`/api/store/purchase-orders/${poId}/documents/${docId}`);
      showToast('Document deleted', 'success');
      setPoDocsByPoId((prev) => ({
        ...prev,
        [poId]: (prev[poId] || []).filter((doc) => doc._id !== docId)
      }));
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to delete document.'), 'error');
    } finally {
      setPoDocBusy(poId, category, false);
    }
  };

  const handlePoRefValueChange = (recordId, field, value) => {
    setReceivingHistory((prev) => prev.map((rec) => {
      if (rec._id !== recordId) return rec;
      if (!rec.purchaseOrderId || typeof rec.purchaseOrderId !== 'object') return rec;
      return {
        ...rec,
        purchaseOrderId: {
          ...rec.purchaseOrderId,
          [field]: value
        }
      };
    }));
  };

  const handleSavePoReferences = async (record) => {
    const po = record.purchaseOrderId;
    if (!po || typeof po !== 'object') return;
    const poId = po._id || po;
    try {
      const res = await axios.patch(`/api/store/purchase-orders/${poId}/shipping-references`, {
        dnNo: po.dnNo || '',
        awbNo: po.awbNo || '',
        boeNo: po.boeNo || '',
        invoiceNo: po.invoiceNo || ''
      });
      setReceivingHistory((prev) => prev.map((rec) => {
        if (rec._id !== record._id) return rec;
        return {
          ...rec,
          purchaseOrderId: {
            ...rec.purchaseOrderId,
            dnNo: res.data?.dnNo || '',
            awbNo: res.data?.awbNo || '',
            boeNo: res.data?.boeNo || '',
            invoiceNo: res.data?.invoiceNo || ''
          }
        };
      }));
      showToast('PO references saved', 'success');
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to save PO references.'), 'error');
    }
  };

  const refreshPoInReceivingHistory = async (poId) => {
    if (!poId) return;
    try {
      const res = await axios.get(`/api/store/purchase-orders/${poId}`);
      const po = res.data;
      setReceivingHistory((prev) => prev.map((rec) => {
        const currentPo = rec.purchaseOrderId;
        if (!currentPo || typeof currentPo !== 'object') return rec;
        if (String(currentPo._id) !== String(poId)) return rec;
        return { ...rec, purchaseOrderId: po };
      }));
    } catch {
      // Keep current receiving list state if refresh fails.
    }
  };

  const handlePoShipmentValueChange = (recordId, shipmentIdx, field, value) => {
    setReceivingHistory((prev) => prev.map((rec) => {
      if (rec._id !== recordId) return rec;
      if (!rec.purchaseOrderId || typeof rec.purchaseOrderId !== 'object') return rec;
      const currentShipments = Array.isArray(rec.purchaseOrderId.shipments) ? rec.purchaseOrderId.shipments : [];
      const nextShipments = currentShipments.map((shipment, idx) => (
        idx === shipmentIdx ? { ...shipment, [field]: value } : shipment
      ));
      return {
        ...rec,
        purchaseOrderId: {
          ...rec.purchaseOrderId,
          shipments: nextShipments
        }
      };
    }));
  };

  const handleAddPoShipmentRow = (recordId) => {
    setReceivingHistory((prev) => prev.map((rec) => {
      if (rec._id !== recordId) return rec;
      if (!rec.purchaseOrderId || typeof rec.purchaseOrderId !== 'object') return rec;
      const currentShipments = Array.isArray(rec.purchaseOrderId.shipments) ? rec.purchaseOrderId.shipments : [];
      return {
        ...rec,
        purchaseOrderId: {
          ...rec.purchaseOrderId,
          shipments: [
            ...currentShipments,
            {
              shipmentNo: '',
              shipmentDate: '',
              dnNo: '',
              awbNo: '',
              boeNo: '',
              invoiceNo: '',
              remarks: '',
              dnDocuments: [],
              awbDocuments: [],
              boeDocuments: [],
              invoiceDocuments: []
            }
          ]
        }
      };
    }));
  };

  const handleRemovePoShipmentRow = (recordId, shipmentIdx) => {
    setReceivingHistory((prev) => prev.map((rec) => {
      if (rec._id !== recordId) return rec;
      if (!rec.purchaseOrderId || typeof rec.purchaseOrderId !== 'object') return rec;
      const currentShipments = Array.isArray(rec.purchaseOrderId.shipments) ? rec.purchaseOrderId.shipments : [];
      return {
        ...rec,
        purchaseOrderId: {
          ...rec.purchaseOrderId,
          shipments: currentShipments.filter((_, idx) => idx !== shipmentIdx)
        }
      };
    }));
  };

  const handleSavePoShipments = async (record) => {
    const po = record.purchaseOrderId;
    if (!po || typeof po !== 'object') return;
    const poId = po._id || po;
    try {
      const payload = (po.shipments || []).map((shipment) => ({
        _id: shipment._id,
        shipmentNo: shipment.shipmentNo || '',
        shipmentDate: shipment.shipmentDate || null,
        dnNo: shipment.dnNo || '',
        awbNo: shipment.awbNo || '',
        boeNo: shipment.boeNo || '',
        invoiceNo: shipment.invoiceNo || '',
        remarks: shipment.remarks || ''
      }));
      const res = await axios.patch(`/api/store/purchase-orders/${poId}/shipments`, { shipments: payload });
      setReceivingHistory((prev) => prev.map((rec) => {
        const recPo = rec.purchaseOrderId;
        if (!recPo || typeof recPo !== 'object') return rec;
        if (String(recPo._id) !== String(poId)) return rec;
        return { ...rec, purchaseOrderId: res.data };
      }));
      showToast('PO shipment details saved', 'success');
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to save PO shipment details.'), 'error');
    }
  };

  const setShipmentDocFieldBusy = (shipmentId, docsKey, busy) => {
    const key = `${shipmentId}:${docsKey}`;
    setShipmentDocBusy((prev) => ({ ...prev, [key]: busy }));
  };

  const handlePoShipmentDocUpload = async (poId, shipment, field, file) => {
    if (!poId || !shipment?._id || !file) {
      if (!shipment?._id) showToast('Save shipment rows first, then attach documents.', 'warning');
      return;
    }
    const busyKey = `${shipment._id}:${field.docsKey}`;
    setShipmentDocFieldBusy(shipment._id, field.docsKey, true);
    try {
      const formData = new FormData();
      formData.append('documents', file);
      await axios.post(`/api/store/purchase-orders/${poId}/shipments/${shipment._id}/documents/${field.key}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshPoInReceivingHistory(poId);
      showToast(`${field.label} shipment document uploaded`, 'success');
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to upload shipment document.'), 'error');
    } finally {
      setShipmentDocBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handlePoShipmentDocDelete = async (poId, shipment, field, docId) => {
    if (!poId || !shipment?._id || !docId) return;
    const busyKey = `${shipment._id}:${field.docsKey}`;
    setShipmentDocFieldBusy(shipment._id, field.docsKey, true);
    try {
      await axios.delete(`/api/store/purchase-orders/${poId}/shipments/${shipment._id}/documents/${field.key}/${docId}`);
      await refreshPoInReceivingHistory(poId);
      showToast(`${field.label} shipment document deleted`, 'success');
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to delete shipment document.'), 'error');
    } finally {
      setShipmentDocBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handleOpenDocumentsForRecord = (record) => {
    const label =
      record?.loanDirection === 'LOAN_IN'
        ? (String(record.loanReferenceNumber || '').trim()
          ? `Loan ${record.loanReferenceNumber}`
          : `Record ${(record._id || '').slice(-6)}`)
        : record.purchaseOrderNumber
          ? `PO ${record.purchaseOrderNumber}`
          : `Record ${(record._id || '').slice(-6)}`;
    setDocumentDialog({ open: true, recordId: record._id, label });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateFromFilter('');
    setDateToFilter('');
    setSupplierFilter('ALL');
    setAircraftFilter('ALL');
    setWorkflowFilter('ALL');
  };

  const hasActiveFilters = () =>
    searchTerm !== '' ||
    dateFromFilter !== '' ||
    dateToFilter !== '' ||
    supplierFilter !== 'ALL' ||
    aircraftFilter !== 'ALL' ||
    workflowFilter !== 'ALL';

  const getReceivingRecordAircraftId = (rec) => {
    const po = rec.purchaseOrderId;
    if (po && typeof po === 'object') {
      const id = po.aircraftId?._id ?? po.aircraftId;
      return id != null ? String(id) : null;
    }
    const line = (rec.items || []).find((l) => String(l.aircraftScope || '').toUpperCase() === 'AIRCRAFT' && l.aircraftId);
    const lid = line?.aircraftId?._id ?? line?.aircraftId;
    return lid != null ? String(lid) : null;
  };

  const getReceivingRecordAircraftLabel = (rec) => {
    const po = rec.purchaseOrderId;
    if (po && typeof po === 'object') {
      const fromPo = po.aircraftRegistration || po.aircraftId?.registration;
      if (fromPo) return fromPo;
    }
    const line = (rec.items || []).find(
      (l) => String(l.aircraftScope || '').toUpperCase() === 'AIRCRAFT'
        && String(l.aircraftRegistration || '').trim()
        && String(l.aircraftRegistration).toUpperCase() !== 'GEN'
    );
    return line?.aircraftRegistration || '—';
  };

  const filteredReceiving = useMemo(() => {
    const safeRecords = normalizeReceivingRecords(receivingHistory);
    return safeRecords.filter((rec) => {
      if (!matchesReceivingContext(rec, receivingContext)) return false;
      if (supplierFilter !== 'ALL') {
        const supId = typeof rec.supplier === 'object' ? rec.supplier?._id?.toString() : rec.supplier?.toString();
        if (supId !== supplierFilter) return false;
      }
      if (aircraftFilter !== 'ALL') {
        const acId = getReceivingRecordAircraftId(rec);
        if (acId !== aircraftFilter) return false;
      }
      if (dateFromFilter) {
        const fromDate = new Date(dateFromFilter);
        fromDate.setHours(0, 0, 0, 0);
        const recDate = new Date(rec.receivingDate);
        if (recDate < fromDate) return false;
      }
      if (dateToFilter) {
        const toDate = new Date(dateToFilter);
        toDate.setHours(23, 59, 59, 999);
        const recDate = new Date(rec.receivingDate);
        if (recDate > toDate) return false;
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const supplierName = (rec.supplier?.name || '').toLowerCase();
        const poNumber = (rec.purchaseOrderNumber || '').toLowerCase();
        const loanRef = (rec.loanReferenceNumber || '').toLowerCase();
        const counterparty = (rec.counterpartyCompany || '').toLowerCase();
        const partNumbers = (rec.items || []).map((i) => (i.partNumber || '').toLowerCase()).join(' ');
        const referencePartNumbers = (rec.items || []).map((i) => (i.referencePartNumber || '').toLowerCase()).join(' ');
        const descriptions = (rec.items || []).map((i) => (i.description || '').toLowerCase()).join(' ');
        const receivedByName = `${rec.receivedBy?.firstName || ''} ${rec.receivedBy?.lastName || ''}`.toLowerCase();
        if (
          !supplierName.includes(term) &&
          !poNumber.includes(term) &&
          !loanRef.includes(term) &&
          !counterparty.includes(term) &&
          !partNumbers.includes(term) &&
          !referencePartNumbers.includes(term) &&
          !descriptions.includes(term) &&
          !receivedByName.includes(term)
        )
          return false;
      }
      return true;
    });
  }, [
    receivingHistory,
    receivingContext,
    supplierFilter,
    aircraftFilter,
    dateFromFilter,
    dateToFilter,
    searchTerm
  ]);

  const historyColSpan = (isLoanRentalContext ? 11 : 10) + (isSuperAdmin ? 1 : 0);

  const isHistoryRecordDeletable = (record) => {
    const ws = record?.workflowStatus;
    if (!ws) return false;
    if (isSuperAdmin) return true;
    return ['DRAFT', 'PENDING_INSPECTOR_APPROVAL', 'REJECTED'].includes(ws);
  };

  const deletableFilteredReceiving = useMemo(
    () => filteredReceiving.filter(isHistoryRecordDeletable),
    [filteredReceiving]
  );
  const allDeletableHistorySelected = deletableFilteredReceiving.length > 0
    && deletableFilteredReceiving.every((record) => selectedHistoryIds.includes(String(record._id)));
  const someDeletableHistorySelected = deletableFilteredReceiving.some((record) => selectedHistoryIds.includes(String(record._id)));

  useEffect(() => {
    const visibleIds = new Set(filteredReceiving.map((record) => String(record._id)));
    setSelectedHistoryIds((prev) => {
      const next = prev.filter((id) => visibleIds.has(String(id)));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [filteredReceiving]);

  const toggleHistorySelectAll = () => {
    if (allDeletableHistorySelected) {
      setSelectedHistoryIds([]);
      return;
    }
    setSelectedHistoryIds(deletableFilteredReceiving.map((record) => String(record._id)));
  };

  const toggleHistorySelectOne = (recordId) => {
    const id = String(recordId);
    setSelectedHistoryIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const openHistoryDeleteDialog = (targets) => {
    if (!targets.length) return;
    setHistoryDeleteDialog({ open: true, targets });
  };

  const handleDeleteReceivingHistory = async () => {
    if (!historyDeleteDialog.targets.length) return;
    setHistoryDeleteLoading(true);
    const hasApplied = historyDeleteDialog.targets.some((r) => r.workflowStatus === 'APPROVED');
    const force = isSuperAdmin && hasApplied;
    try {
      const ids = historyDeleteDialog.targets.map((record) => record._id);
      if (ids.length === 1) {
        await axios.delete(storeApi(`/receiving/${ids[0]}${force ? '?force=true' : ''}`));
        showToast(`Receiving record ${recordProcurementReference(historyDeleteDialog.targets[0])} deleted${force ? ' (inventory reversed)' : ''}`, 'success');
      } else {
        const res = await axios.post(storeApi('/receiving/batch-delete'), { ids, force });
        const { deletedCount = 0, failedCount = 0, failed = [] } = res.data || {};
        if (deletedCount > 0 && failedCount === 0) {
          showToast(`${deletedCount} receiving record(s) deleted`, 'success');
        } else if (deletedCount > 0) {
          const failedLabels = failed.map((f) => f.reference || f.recordId).join(', ');
          showToast(`${deletedCount} deleted, ${failedCount} failed (${failedLabels})`, 'warning');
        } else {
          showToast(failed[0]?.message || 'Failed to delete receiving records.', 'error');
        }
      }
      setHistoryDeleteDialog({ open: false, targets: [] });
      setSelectedHistoryIds([]);
      await fetchReceivingHistory();
      await fetchPurchaseOrders();
    } catch (e) {
      showToast(getErrorMessage(e, 'Failed to delete receiving record(s).'), 'error');
    } finally {
      setHistoryDeleteLoading(false);
    }
  };

  useEffect(() => {
    Object.entries(expandedRows).forEach(([recordId, expanded]) => {
      if (!expanded) return;
      const record = receivingHistory.find((rec) => rec && String(rec._id) === String(recordId));
      const po = record?.purchaseOrderId;
      const poId = po && typeof po === 'object' ? po._id : null;
      if (poId && !poDocsByPoId[poId]) {
        fetchPoDocuments(poId);
      }
    });
  }, [expandedRows, receivingHistory, poDocsByPoId, fetchPoDocuments]);

  const handleExportCsv = () => {
    const rows = [];
    filteredReceiving.forEach((record) => {
      const po = record.purchaseOrderId && typeof record.purchaseOrderId === 'object' ? record.purchaseOrderId : null;
      let aircraftId = po?.aircraftId?._id ?? po?.aircraftId ?? '';
      let aircraftRegistration = po?.aircraftId?.registration || po?.aircraftRegistration || '';
      if (!aircraftId && !aircraftRegistration) {
        const line = (record.items || []).find((l) => String(l.aircraftScope || '').toUpperCase() === 'AIRCRAFT');
        aircraftId = line?.aircraftId ? String(line.aircraftId._id ?? line.aircraftId) : '';
        aircraftRegistration = line?.aircraftRegistration || '';
      }
      const supplierCode = record.supplier?.code || '';
      const supplierName = record.supplier?.name || '';
      const receivingDate = record.receivingDate ? new Date(record.receivingDate).toISOString() : '';
      const poOrLoanRef =
        record.loanDirection === 'LOAN_IN'
          ? (record.loanReferenceNumber || '')
          : (record.purchaseOrderNumber || '');
      (record.items || []).forEach((item) => {
        rows.push({
          aircraftId: aircraftId ? String(aircraftId) : '',
          aircraftRegistration,
          purchaseOrderNumber: poOrLoanRef,
          receivingRecordId: record._id || '',
          receivingDate,
          supplierCode,
          supplierName,
          partNumber: item.partNumber || '',
          referencePartNumber: item.referencePartNumber || '',
          description: item.description || '',
          category: normalizeInventoryCategory(item.category),
          orderedQty: Number(item.quantity) || 0,
          receivedQty: Number(item.receivedQuantity) || 0,
          unit: item.unit || '',
          condition: item.condition || '',
          certificateNumber: item.certificateNumber || '',
          batchNumber: item.batchNumber || '',
          serialNumber: item.serialNumber || '',
          shelfLifeUpto: item.shelfLifeNotApplicable
            ? 'N/A'
            : (item.shelfLifeUpto ? shelfToInputValue(item.shelfLifeUpto) : ''),
          remarks: item.remarks || '',
          defaultWarehouse: item.location?.warehouse || '',
          defaultShelf: item.location?.shelf || '',
          defaultBin: item.location?.bin || ''
        });
      });
    });

    if (rows.length === 0) {
      showToast('No filtered receiving rows to export', 'warning');
      return;
    }

    const csvBody = [
      CSV_COLUMNS.join(','),
      ...rows.map((row) => CSV_COLUMNS.map((key) => escapeCsvCell(row[key])).join(','))
    ].join('\n');

    const blob = new Blob([csvBody], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const selectedAircraft = aircraftList.find((a) => String(a._id) === String(aircraftFilter));
    const aircraftSuffix = selectedAircraft?.registration
      ? String(selectedAircraft.registration).replace(/[^a-zA-Z0-9_-]/g, '_')
      : aircraftFilter !== 'ALL'
      ? String(aircraftFilter).slice(-6)
      : 'all';
    const datePart = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.setAttribute('download', `receiving_aircraft_${aircraftSuffix}_${datePart}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast(`Exported ${rows.length} row(s)`, 'success');
  };

  const getReceivingStatusInfo = (record) => {
    if (record.workflowStatus === 'DRAFT') {
      return { label: 'DRAFT', color: 'default' };
    }
    if (record.workflowStatus === 'PENDING_INSPECTOR_APPROVAL') {
      return { label: 'PENDING INSPECTION', color: 'info' };
    }
    if (record.workflowStatus === 'REJECTED') {
      if (record.supersededBy || record.resubmittedAt) {
        return { label: 'REJECTED · RESUBMITTED', color: 'default' };
      }
      return { label: 'REJECTED', color: 'error' };
    }

    const po = record.purchaseOrderId && typeof record.purchaseOrderId === 'object' ? record.purchaseOrderId : null;
    if (!po) {
      return { label: 'RECEIVED', color: 'default' };
    }

    const poStatus = po.status || '';
    const totalRemaining = Array.isArray(po.lineItems)
      ? po.lineItems.reduce((sum, line) => {
          const ordered = Number(line.quantity) || 0;
          const received = Number(line.receivedQuantity) || 0;
          return sum + Math.max(ordered - received, 0);
        }, 0)
      : 0;

    if (poStatus === 'RECEIVED' || poStatus === 'CLOSED' || totalRemaining === 0) {
      return { label: 'COMPLETE', color: 'success' };
    }

    if (poStatus === 'ISSUED' || poStatus === 'PARTIALLY_RECEIVED') {
      return { label: `INCOMPLETE (${totalRemaining})`, color: 'warning' };
    }

    return { label: poStatus || 'RECEIVED', color: 'default' };
  };

  return (
    <Box>
      {!receivingContext && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Receiving and Inspection
          </Typography>
          <Alert severity="info" sx={{ mb: 1 }}>
            All receiving submissions require Store Inspector/AM approval for cross-checking. Nothing is added to inventory
            or PO received quantities until approval is completed. Use Submit for inspection — CSV export is for reporting only.
          </Alert>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          {canSubmitReceiving && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleSaveDraft}
            disabled={autosaveState.saving || loading}
          >
            {autosaveState.saving ? 'Saving Draft...' : 'Save as Draft'}
          </Button>
          )}
          <Typography
            variant="caption"
            color={autosaveState.error ? 'error.main' : 'text.secondary'}
            sx={{ display: 'block', mt: 0.5 }}
          >
            {autosaveState.error
              ? autosaveState.error
              : autosaveState.lastSavedAt
                ? `Draft last saved at ${autosaveState.lastSavedAt.toLocaleTimeString()}`
                : 'Use "Save as Draft" to preserve work in progress.'}
          </Typography>
        </Box>
      </Box>
      )}
      {receivingContext && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Alert
            severity={receivingContextBannerSeverity(receivingContext, isLoanReturnReceiving)}
            sx={{ flex: 1, minWidth: 280 }}
            icon={false}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2" component="span">
                {receivingContextFormTitle(receivingContext, loanUiOptions)}
              </Typography>
              {isLoanReturnReceiving && (
                <Chip
                  size="small"
                  label={loanReceivingPurposeLabel(loanReceivingMeta.loanReceivingPurpose)}
                  color={loanReceivingPurposeChipColor(loanReceivingMeta.loanReceivingPurpose)}
                />
              )}
              {!isLoanReturnReceiving && (
                <Chip size="small" label={receivingContextHistoryLabel(receivingContext)} variant="outlined" />
              )}
            </Box>
            <Typography variant="body2">
              {receivingContextFormDescription(receivingContext, loanUiOptions)}
            </Typography>
          </Alert>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            {canSubmitReceiving && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveDraft}
              disabled={autosaveState.saving || loading}
            >
              {autosaveState.saving ? 'Saving Draft...' : 'Save as Draft'}
            </Button>
            )}
            <Typography
              variant="caption"
              color={autosaveState.error ? 'error.main' : 'text.secondary'}
              sx={{ display: 'block', mt: 0.5, maxWidth: 220 }}
            >
              {autosaveState.error
                ? autosaveState.error
                : autosaveState.lastSavedAt
                  ? `Draft saved ${autosaveState.lastSavedAt.toLocaleTimeString()}`
                  : 'Save draft to keep progress.'}
            </Typography>
          </Box>
        </Box>
      )}

      <Paper sx={{ p: 3 }} ref={formSectionRef}>
        {formEditMode && (
          <Alert
            severity={formEditMode === 'resubmit' ? 'warning' : 'info'}
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearFormEdit}>
                Cancel
              </Button>
            }
          >
            {formEditMode === 'draft' && (
              <>Continuing draft <strong>{formEditRecordRef}</strong>. Save as draft or submit when ready.</>
            )}
            {formEditMode === 'pending' && (
              <>Editing your pending submission <strong>{formEditRecordRef}</strong>. Submit updates the pending record.</>
            )}
            {formEditMode === 'resubmit' && (
              <>Revising rejected submission <strong>{formEditRecordRef}</strong>. Submit creates a new approval request.</>
            )}
          </Alert>
        )}
        {receivingContext && (
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            {receivingContextFormTitle(receivingContext, loanUiOptions)}
          </Typography>
        )}
        {isLoanRentalContext && isLoanReturnReceiving && loanReceivingMeta.lockMeta && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Linked loan (read-only)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Recipient:</strong> {loanReceivingMeta.counterpartyCompany || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Loan reference:</strong> {loanReceivingMeta.loanReferenceNumber || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Return due:</strong>{' '}
                  {loanReceivingMeta.returnDueDate
                    ? new Date(loanReceivingMeta.returnDueDate).toLocaleDateString()
                    : '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Supplier</InputLabel>
              <Select
                value={formData.supplier}
                label="Supplier"
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                required
                disabled={suppliers.length === 0}
              >
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier._id} value={supplier._id}>
                    {supplier.name} ({supplier.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {suppliers.length === 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No suppliers found. Add one to start receiving inventory.
              </Alert>
            )}
          </Grid>
          {isLoanRentalContext && !isLoanReturnReceiving && (
            <LoanReceivingMetaFields
              meta={loanReceivingMeta}
              onMetaChange={mergeLoanMeta}
              onProfileApply={handleLoanProfileApply}
              lockMeta={loanReceivingMeta.lockMeta}
            />
          )}
          {isLoanRentalContext && isLoanReturnReceiving && !loanReceivingMeta.lockMeta && (
            <LoanReceivingMetaFields
              meta={loanReceivingMeta}
              onMetaChange={mergeLoanMeta}
              onProfileApply={handleLoanProfileApply}
              lockMeta={false}
            />
          )}
          {!contextHidesPurchaseOrder && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <FormControl fullWidth>
                <InputLabel>Purchase Order</InputLabel>
                <Select
                  value={selectedPoId || 'custom'}
                  label="Purchase Order"
                  onChange={(e) => handlePoSelect(e.target.value === 'custom' ? '' : e.target.value)}
                >
                  <MenuItem value="custom">Custom PO number</MenuItem>
                  {purchaseOrders.map((po) => (
                    <MenuItem key={po._id} value={po._id}>
                      {po.poNumber} – {po.orderTo?.name || '—'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isSuperAdmin && selectedPoId && (
                <Tooltip title="Delete purchase order">
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      const po = purchaseOrders.find((p) => String(p._id) === String(selectedPoId));
                      openPoDeleteDialog(selectedPoId, po?.poNumber || formData.purchaseOrderNumber);
                    }}
                    sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                  >
                    Delete PO
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Grid>
          )}
          {showAircraftScopeField && (
            <Grid
              item
              xs={12}
              md={isLoanRentalContext || isFromAircraftContext ? 12 : 6}
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
            >
              <Autocomplete
                fullWidth
                options={receivingAircraftOptions}
                value={selectedReceivingAircraftOption}
                onChange={(_, next) => {
                  if (!next) {
                    setSelectedReceivingAircraft(isFromAircraftContext ? '' : 'GEN');
                    return;
                  }
                  setSelectedReceivingAircraft(String(next._id));
                }}
                getOptionLabel={formatReceivingAircraftOptionLabel}
                isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
                renderOption={(props, option) => (
                  <li {...props} key={String(option._id)}>
                    {formatReceivingAircraftOptionLabel(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isLoanRentalContext ? 'Aircraft' : 'Aircraft Scope'}
                    required={isFromAircraftContext}
                    helperText={
                      isLoanRentalContext
                        ? 'Select GEN for common stock, or a registration for aircraft-specific receipt'
                        : undefined
                    }
                  />
                )}
                ListboxProps={{ style: { maxHeight: 320 } }}
              />
            </Grid>
          )}
          {!contextHidesPurchaseOrder && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Purchase Order Number"
              value={formData.purchaseOrderNumber}
              onChange={(e) => setFormData({ ...formData, purchaseOrderNumber: e.target.value })}
              placeholder={selectedPoId ? '' : 'Enter PO number if not linked above'}
              InputProps={{ readOnly: !!selectedPoId }}
            />
          </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required={isLoanReturnReceiving}
              label={isLoanReturnReceiving ? 'Return reason (required)' : 'Discrepancies (if any)'}
              value={formData.discrepancies}
              onChange={(e) => setFormData({ ...formData, discrepancies: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>

        <ReceivingItemsGrid
          items={items}
          onItemsChange={setItems}
          selectedPoId={selectedPoId}
          certificateFilesByRow={certificateFilesByRow}
          certificateServerDocsByRow={certificateServerDocsByRow}
          onCertificateServerDownload={handleCertificateServerDownload}
          downloadingCertificateRowKey={downloadingCertificateRowKey}
          onCertificateFileChange={handleCertificateFileChange}
          onCertificatePendingRemove={handleCertificatePendingRemove}
          onRemoveItem={handleRemoveItem}
          aircraftList={aircraftList}
          headerAircraft={receivingHeaderAircraft}
          title={isLoanReturnReceiving ? 'Return lines' : 'Items'}
          emptyMessage={
            isLoanInReturnReceiving
              ? 'Return lines are prefilled from the loan. Adjust return quantities if needed.'
              : isLoanOutReturnReceiving
                ? 'Add items being returned from the loan-out issuance.'
                : isLoanRentalContext
                  ? 'Add borrowed parts to receive into store inventory.'
                  : 'Add items to receive'
          }
        />


        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {!canSubmitReceiving && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
              Read-only access — Store Executive or Inspector submits receiving.
            </Typography>
          )}
          {showSubmitWithoutPo && canSubmitReceiving && !editingPendingRecordId && (
            <Button
              variant="outlined"
              onClick={handleSubmitWithoutPo}
              disabled={loading || items.length === 0 || !formData.supplier}
            >
              {loading ? 'Submitting...' : 'Submit without PO'}
            </Button>
          )}
          {canSubmitReceiving && (
            <Button
              variant="contained"
              color={isLoanReturnReceiving ? 'warning' : 'primary'}
              onClick={() => handleSubmit()}
              disabled={loading || items.length === 0 || !formData.supplier}
            >
              {loading
                ? 'Submitting...'
                : editingPendingRecordId
                  ? 'Update pending submission'
                  : receivingContextSubmitLabel(receivingContext, loanUiOptions)}
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Manage Receiving Documents
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter an existing receiving record ID to upload or review certificates, packing lists, and inspection documents.
        </Typography>
        {lastReceivingSummary && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="subtitle2">
                Latest Record ID: {lastReceivingSummary.id}
              </Typography>
              <Typography variant="caption">
                Supplier: {lastReceivingSummary.supplier || 'Unknown'} {lastReceivingSummary.supplierCode ? `(${lastReceivingSummary.supplierCode})` : ''} ·{' '}
                {lastReceivingSummary.loanDirection === 'LOAN_IN' ? 'Loan ref: ' : 'PO: '}
                {lastReceivingSummary.loanDirection === 'LOAN_IN'
                  ? (lastReceivingSummary.loanReferenceNumber || '—')
                  : (lastReceivingSummary.purchaseOrderNumber || '—')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={() =>
                  setDocumentDialog({
                    open: true,
                    recordId: lastReceivingSummary.id,
                    label:
                      lastReceivingSummary.loanDirection === 'LOAN_IN'
                        ? (lastReceivingSummary.loanReferenceNumber
                          ? `Loan ${lastReceivingSummary.loanReferenceNumber}`
                          : `Record ${lastReceivingSummary.id.slice(-6)}`)
                        : lastReceivingSummary.purchaseOrderNumber
                          ? `PO ${lastReceivingSummary.purchaseOrderNumber}`
                          : `Record ${lastReceivingSummary.id.slice(-6)}`
                  })
                }
              >
                Open Documents
              </Button>
            </Box>
          </Alert>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Receiving Record ID"
              value={documentRecordId}
              onChange={(e) => setDocumentRecordId(e.target.value)}
              placeholder="e.g., 64bf2a1c1f..."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Tooltip title="Open documents manager">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AttachFile />}
                  onClick={handleOpenDocuments}
                  disabled={!documentRecordId}
                >
                  Manage Documents
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Receiving History Section */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {receivingContext
              ? `Receiving History — ${receivingContextHistoryLabel(receivingContext)}`
              : 'Receiving History'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isSuperAdmin && selectedHistoryIds.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Delete />}
                onClick={() => {
                  const targets = filteredReceiving.filter((record) => selectedHistoryIds.includes(String(record._id)));
                  openHistoryDeleteDialog(targets);
                }}
              >
                Delete selected ({selectedHistoryIds.length})
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setArchiveFieldsDialogOpen(true)}
            >
              Archive Fields
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchReceivingHistory}
              disabled={loadingReceivingHistory}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Filters
              </Typography>
              {hasActiveFilters() && (
                <Chip
                label={`${[searchTerm && 'Search', dateFromFilter && 'Date From', dateToFilter && 'Date To', supplierFilter !== 'ALL' && 'Supplier', aircraftFilter !== 'ALL' && 'Aircraft', workflowFilter !== 'ALL' && 'Workflow'].filter(Boolean).length} active`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportCsv}
                disabled={loadingReceivingHistory || filteredReceiving.length === 0}
              >
                Export CSV
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={filtersExpanded ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                {filtersExpanded ? 'Hide' : 'Show'} Advanced
              </Button>
              {hasActiveFilters() && (
                <Button size="small" variant="outlined" color="secondary" startIcon={<Clear />} onClick={handleClearFilters}>
                  Clear All
                </Button>
              )}
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by PO, loan ref, recipient, part number, description, supplier, received by..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={supplierFilter}
                  label="Supplier"
                  onChange={(e) => setSupplierFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All Suppliers</MenuItem>
                  {suppliers.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.name} {s.code ? `(${s.code})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: { xs: '100%', sm: 260 } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Aircraft</InputLabel>
                <Select
                  value={aircraftFilter}
                  label="Aircraft"
                  onChange={(e) => setAircraftFilter(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { minWidth: 280 } } }}
                >
                  <MenuItem value="ALL">All aircraft</MenuItem>
                  {aircraftList.map((a) => (
                    <MenuItem key={a._id} value={String(a._id)}>
                      {a.registration || a._id}
                      {a.model || a.modelVariant ? ` (${a.model || a.modelVariant})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Workflow</InputLabel>
                <Select
                  value={workflowFilter}
                  label="Workflow"
                  onChange={(e) => setWorkflowFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All records</MenuItem>
                  <MenuItem value="DRAFT">Drafts</MenuItem>
                  <MenuItem value="PENDING">Pending inspection</MenuItem>
                  <MenuItem value="APPROVED">Applied / legacy</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Collapse in={filtersExpanded} timeout="auto">
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date From"
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date To"
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Paper>

        {(receivingContext || hasActiveFilters()) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {receivingContext
                ? `Showing ${filteredReceiving.length} ${receivingContextHistoryLabel(receivingContext)} record${filteredReceiving.length === 1 ? '' : 's'}`
                : `Showing ${filteredReceiving.length} of ${receivingHistory.length} receiving records`}
            </Typography>
          </Box>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {isSuperAdmin && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allDeletableHistorySelected}
                      indeterminate={someDeletableHistorySelected && !allDeletableHistorySelected}
                      onChange={toggleHistorySelectAll}
                      disabled={loadingReceivingHistory || deletableFilteredReceiving.length === 0}
                      inputProps={{ 'aria-label': 'Select all deletable receiving records' }}
                    />
                  </TableCell>
                )}
                <TableCell width="50px" />
                <TableCell>Receiving Date</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>{historyReferenceColumnLabel}</TableCell>
                {isLoanRentalContext && <TableCell sx={{ minWidth: 120 }}>Type</TableCell>}
                <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>Aircraft</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Received By</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Discrepancies</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingReceivingHistory ? (
                <TableRow>
                    <TableCell colSpan={historyColSpan} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Loading receiving history...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredReceiving.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={historyColSpan} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No receiving records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReceiving.map((record) => (
                  <React.Fragment key={record._id}>
                    {(() => {
                      const statusInfo = getReceivingStatusInfo(record);
                      return (
                    <TableRow
                      selected={isSuperAdmin && selectedHistoryIds.includes(String(record._id))}
                      hover
                    >
                      {isSuperAdmin && (
                        <TableCell padding="checkbox">
                          <Tooltip
                            title={
                              isHistoryRecordDeletable(record)
                                ? (record.workflowStatus === 'APPROVED' ? 'Select for force delete (will reverse inventory)' : 'Select for batch delete')
                                : 'Only draft, pending, or rejected records can be deleted'
                            }
                          >
                            <span>
                              <Checkbox
                                size="small"
                                checked={selectedHistoryIds.includes(String(record._id))}
                                onChange={() => toggleHistorySelectOne(record._id)}
                                disabled={!isHistoryRecordDeletable(record)}
                                inputProps={{ 'aria-label': `Select receiving record ${recordProcurementReference(record)}` }}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleRowExpansion(record._id)}>
                          {expandedRows[record._id] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{new Date(record.receivingDate).toLocaleString()}</TableCell>
                      <TableCell>
                        {record.supplier
                          ? `${record.supplier.name || 'N/A'}${record.supplier.code ? ` (${record.supplier.code})` : ''}`
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{recordProcurementReference(record)}</TableCell>
                      {isLoanRentalContext && (
                        <TableCell>
                          {(() => {
                            const purposeChip = historyRowLoanPurposeChip(record);
                            return purposeChip ? (
                              <Chip
                                size="small"
                                label={purposeChip.label}
                                color={purposeChip.color}
                                variant="outlined"
                              />
                            ) : '—';
                          })()}
                        </TableCell>
                      )}
                      <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
                        {getReceivingRecordAircraftLabel(record)}
                      </TableCell>
                      <TableCell>{(record.items || []).length} item(s)</TableCell>
                      <TableCell>
                        {record.receivedBy
                          ? `${record.receivedBy.firstName || ''} ${record.receivedBy.lastName || ''}`.trim() || 'N/A'
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusInfo.label}
                          color={statusInfo.color}
                          variant={statusInfo.color === 'default' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>
                        {record.discrepancies ? (
                          <Typography noWrap title={record.discrepancies} variant="body2">
                            {record.discrepancies}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {canSubmitReceiving && record.workflowStatus === 'DRAFT' && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => loadRecordIntoForm(record, 'draft')}
                            >
                              Continue draft
                            </Button>
                          )}
                          {canSubmitReceiving
                            && record.workflowStatus === 'PENDING_INSPECTOR_APPROVAL'
                            && isRecordSubmitter(record) && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => loadRecordIntoForm(record, 'pending')}
                            >
                              Edit
                            </Button>
                          )}
                          {canSubmitReceiving
                            && record.workflowStatus === 'REJECTED'
                            && !record.supersededBy
                            && !record.resubmittedAt && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => loadRecordIntoForm(record, 'resubmit')}
                            >
                              Revise & resubmit
                            </Button>
                          )}
                          {canInspectApprove
                            && record.workflowStatus === 'PENDING_INSPECTOR_APPROVAL'
                            && !isRecordSubmitter(record) && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              disabled={approvalLoading}
                              onClick={() => openApprovalDialog(record)}
                            >
                              Review
                            </Button>
                          )}
                          <Tooltip title="Manage documents (certificates, packing list, inspection)">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AttachFile />}
                              onClick={() => handleOpenDocumentsForRecord(record)}
                            >
                              Documents
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                      );
                    })()}
                    <TableRow>
                    <TableCell colSpan={isLoanRentalContext ? 11 : 10} sx={{ py: 0 }}>
                        <Collapse in={expandedRows[record._id]} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                            {record.purchaseOrderId && typeof record.purchaseOrderId === 'object' && (
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                  <Typography variant="subtitle2">
                                    PO Shipping / Customs References
                                    {record.purchaseOrderId.poNumber ? ` — ${record.purchaseOrderId.poNumber}` : ''}
                                  </Typography>
                                  {isSuperAdmin && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => openPoDeleteDialog(
                                        record.purchaseOrderId._id,
                                        record.purchaseOrderId.poNumber || record.purchaseOrderNumber
                                      )}
                                    >
                                      Delete PO
                                    </Button>
                                  )}
                                </Box>
                                <Grid container spacing={1}>
                                  {PO_REFERENCE_FIELDS.map((field) => {
                                    const po = record.purchaseOrderId;
                                    const poId = po._id;
                                    const latestDoc = getLatestPoDocByCategory(poId, field.docCategory);
                                    const docBusy = Boolean(poDocBusyByCategory[`${poId}:${field.docCategory}`]);
                                    return (
                                      <Grid item xs={12} md={6} key={`${record._id}-${field.key}`}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label={field.label}
                                          value={po[field.key] || ''}
                                          onChange={(e) => handlePoRefValueChange(record._id, field.key, e.target.value)}
                                        />
                                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            component="label"
                                            startIcon={<Upload fontSize="small" />}
                                            onClick={() => fetchPoDocuments(poId)}
                                            disabled={docBusy}
                                          >
                                            Attach
                                            <input
                                              type="file"
                                              hidden
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) handlePoRefDocUpload(poId, field.docCategory, file);
                                                e.target.value = '';
                                              }}
                                            />
                                          </Button>
                                          <IconButton
                                            size="small"
                                            disabled={!latestDoc || docBusy || downloadingDocKey === latestDoc._id}
                                            onClick={() => handleDownloadAttachment(
                                              `/api/store/purchase-orders/${poId}/documents/${latestDoc._id}/download`,
                                              latestDoc.originalName || latestDoc.filename,
                                              latestDoc._id
                                            )}
                                          >
                                            <Download fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            disabled={!latestDoc || docBusy}
                                            onClick={() => handlePoRefDocDelete(poId, field.docCategory, latestDoc._id)}
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                          <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => {
                                              fetchPoDocuments(poId);
                                              setPoFieldDocsDialog({
                                                open: true,
                                                poId,
                                                category: field.docCategory,
                                                label: field.label
                                              });
                                            }}
                                          >
                                            View all
                                          </Button>
                                        </Box>
                                        {latestDoc && (
                                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                            {latestDoc.originalName || latestDoc.filename}
                                          </Typography>
                                        )}
                                      </Grid>
                                    );
                                  })}
                                  <Grid item xs={12}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleSavePoReferences(record)}
                                    >
                                      Save PO References
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                            {record.purchaseOrderId && typeof record.purchaseOrderId === 'object' && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  PO Shipment Details
                                </Typography>
                                {((record.purchaseOrderId.shipments || []).length === 0) && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    No shipment rows added yet.
                                  </Typography>
                                )}
                                <Grid container spacing={1}>
                                  {(record.purchaseOrderId.shipments || []).map((shipment, shipmentIdx) => (
                                    <React.Fragment key={`${record._id}-shipment-${shipment._id || shipmentIdx}`}>
                                      <Grid item xs={12} sm={6} md={2}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Shipment No"
                                          value={shipment.shipmentNo || ''}
                                          onChange={(e) => handlePoShipmentValueChange(record._id, shipmentIdx, 'shipmentNo', e.target.value)}
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={6} md={2}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="date"
                                          label="Shipment Date"
                                          value={shipment.shipmentDate ? String(shipment.shipmentDate).slice(0, 10) : ''}
                                          onChange={(e) => handlePoShipmentValueChange(record._id, shipmentIdx, 'shipmentDate', e.target.value)}
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      </Grid>
                                      {SHIPMENT_FIELDS.map((field) => {
                                        const docs = shipment[field.docsKey] || [];
                                        const latestDoc = docs[docs.length - 1];
                                        const docBusy = Boolean(shipmentDocBusy[`${shipment._id}:${field.docsKey}`]);
                                        return (
                                          <Grid item xs={12} sm={6} md={2} key={`${record._id}-${shipmentIdx}-${field.key}`}>
                                            <TextField
                                              fullWidth
                                              size="small"
                                              label={field.label}
                                              value={shipment[field.key] || ''}
                                              onChange={(e) => handlePoShipmentValueChange(record._id, shipmentIdx, field.key, e.target.value)}
                                            />
                                            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                component="label"
                                                startIcon={<Upload fontSize="small" />}
                                                disabled={docBusy || !shipment._id}
                                              >
                                                Attach
                                                <input
                                                  type="file"
                                                  hidden
                                                  onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handlePoShipmentDocUpload(record.purchaseOrderId._id, shipment, field, file);
                                                    e.target.value = '';
                                                  }}
                                                />
                                              </Button>
                                              <IconButton
                                                size="small"
                                                disabled={!latestDoc || docBusy || downloadingDocKey === latestDoc._id}
                                                onClick={() => handleDownloadAttachment(
                                                  `/api/store/purchase-orders/${record.purchaseOrderId._id}/shipments/${shipment._id}/documents/${field.key}/${latestDoc._id}/download`,
                                                  latestDoc.originalName || latestDoc.filename,
                                                  latestDoc._id
                                                )}
                                              >
                                                <Download fontSize="small" />
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                disabled={!latestDoc || docBusy}
                                                onClick={() => handlePoShipmentDocDelete(record.purchaseOrderId._id, shipment, field, latestDoc._id)}
                                              >
                                                <Delete fontSize="small" />
                                              </IconButton>
                                            </Box>
                                            {latestDoc && (
                                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                {latestDoc.originalName || latestDoc.filename}
                                              </Typography>
                                            )}
                                          </Grid>
                                        );
                                      })}
                                      <Grid item xs={12} md={10}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Remarks"
                                          value={shipment.remarks || ''}
                                          onChange={(e) => handlePoShipmentValueChange(record._id, shipmentIdx, 'remarks', e.target.value)}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                        <Button color="error" onClick={() => handleRemovePoShipmentRow(record._id, shipmentIdx)}>
                                          Remove
                                        </Button>
                                      </Grid>
                                    </React.Fragment>
                                  ))}
                                  <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      <Button size="small" variant="outlined" onClick={() => handleAddPoShipmentRow(record._id)}>
                                        Add Shipment
                                      </Button>
                                      <Button size="small" variant="outlined" onClick={() => handleSavePoShipments(record)}>
                                        Save Shipment Details
                                      </Button>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                            <Typography variant="subtitle2" gutterBottom>
                              Received Items
                            </Typography>
                            <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 900 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>Part Number</TableCell>
                                  <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>Reference Part Number</TableCell>
                                  {!isHistoryFieldArchived('description') && (
                                    <TableCell sx={{ minWidth: 180 }}>Description</TableCell>
                                  )}
                                  {!isHistoryFieldArchived('orderedQty') && <TableCell>Ordered</TableCell>}
                                  {!isHistoryFieldArchived('receivedQty') && <TableCell>Received</TableCell>}
                                  <TableCell sx={{ minWidth: 160, whiteSpace: 'nowrap' }}>PRI / Tag</TableCell>
                                  {!isHistoryFieldArchived('unit') && <TableCell>Unit</TableCell>}
                                  {!isHistoryFieldArchived('remainingQty') && <TableCell>Remaining</TableCell>}
                                  {!isHistoryFieldArchived('condition') && <TableCell>Condition</TableCell>}
                                  {!isHistoryFieldArchived('storeType') && <TableCell>Store Type</TableCell>}
                                  {!isHistoryFieldArchived('location') && <TableCell>Location</TableCell>}
                                  {!isHistoryFieldArchived('certificateNumber') && (
                                    <TableCell>Certificate</TableCell>
                                  )}
                                  {!isHistoryFieldArchived('batchSerial') && <TableCell>Batch / Serial</TableCell>}
                                  {!isHistoryFieldArchived('shelfLife') && <TableCell>Shelf Life Upto</TableCell>}
                                  {!isHistoryFieldArchived('remarks') && <TableCell>Remarks</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(record.items || []).map((item, idx) => (
                                  (() => {
                                    const orderedQty = Number(item.quantity) || 0;
                                    const receivedQty = Number(item.receivedQuantity) || 0;
                                    const remainingQty = orderedQty - receivedQty;
                                    const certDocs = findReceivingCertificateDocuments(record.documents, item.partNumber);
                                    const certDoc = certDocs[0] || null;
                                    return (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                      {item.partNumber || '—'}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                      {item.referencePartNumber || '—'}
                                    </TableCell>
                                    {!isHistoryFieldArchived('description') && (
                                      <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 280 }}>
                                        {item.description || '—'}
                                      </TableCell>
                                    )}
                                    {!isHistoryFieldArchived('orderedQty') && (
                                      <TableCell>{item.quantity ?? '—'}</TableCell>
                                    )}
                                    {!isHistoryFieldArchived('receivedQty') && (
                                      <TableCell>
                                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                                          {item.receivedQuantity ?? '—'}
                                        </Typography>
                                      </TableCell>
                                    )}
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                          {formatLinePriDisplay(item)}
                                        </Typography>
                                        {record.workflowStatus === 'APPROVED' && collectLinePriNumbersForTag(item).length > 0 && Number(item.receivedQuantity) > 0 && (
                                          <Tooltip title="Download PRI / store acceptance tag PDF">
                                            <span>
                                              <IconButton
                                                size="small"
                                                disabled={downloadingPriKey === `${record._id}:${idx}`}
                                                onClick={() => handleDownloadPriTagPdf(record._id, idx, item)}
                                              >
                                                <PictureAsPdf fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </TableCell>
                                    {!isHistoryFieldArchived('unit') && (
                                      <TableCell>{unitMetricLabel(item.unit)}</TableCell>
                                    )}
                                    {!isHistoryFieldArchived('remainingQty') && (
                                      <TableCell>
                                        <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                                          {remainingQty}
                                        </Typography>
                                      </TableCell>
                                    )}
                                    {!isHistoryFieldArchived('condition') && (
                                      <TableCell>{formatPartConditionLabel(item.condition)}</TableCell>
                                    )}
                                    {!isHistoryFieldArchived('storeType') && (
                                      <TableCell>
                                        {formatStoreTypeLabel(item.storeType)}
                                      </TableCell>
                                    )}
                                    {!isHistoryFieldArchived('location') && (
                                      <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 220 }}>
                                        {[
                                          formatReceivingLocation(item.location),
                                          item.locationText
                                        ].filter(Boolean).join(' · ') || '—'}
                                      </TableCell>
                                    )}
                                    {!isHistoryFieldArchived('certificateNumber') && (
                                      <TableCell>
                                        <CertificateField
                                          certificateNumber={item.certificateNumber}
                                          document={certDoc}
                                          documents={certDocs}
                                          onDownloadDocument={
                                            certDocs.length
                                              ? (doc) => handleReceivingHistoryCertificateDownload(
                                                record._id,
                                                doc,
                                                `${record._id}:${idx}:${doc._id}`
                                              )
                                              : null
                                          }
                                          downloadingDocId={
                                            downloadingCertificateRowKey.startsWith(`${record._id}:${idx}:`)
                                              ? downloadingCertificateRowKey.slice(`${record._id}:${idx}:`.length)
                                              : ''
                                          }
                                        />
                                      </TableCell>
                                    )}
                                    {!isHistoryFieldArchived('batchSerial') && (
                                      <TableCell>
                                        {[item.batchNumber, item.serialNumber].filter(Boolean).join(' / ') || '—'}
                                      </TableCell>
                                    )}
                                    {!isHistoryFieldArchived('shelfLife') && (
                                      <TableCell>
                                        {item.shelfLifeNotApplicable
                                          ? 'N/A'
                                          : (item.shelfLifeUpto ? new Date(item.shelfLifeUpto).toLocaleDateString() : '—')}
                                      </TableCell>
                                    )}
                                    {!isHistoryFieldArchived('remarks') && (
                                      <TableCell>{item.remarks || '—'}</TableCell>
                                    )}
                                  </TableRow>
                                    );
                                  })()
                                ))}
                              </TableBody>
                            </Table>
                            </TableContainer>
                            {record.discrepancies && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  Discrepancies
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  {record.discrepancies}
                                </Typography>
                              </>
                            )}
                            {record.inspectorNotes && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  Inspector notes
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {record.inspectorNotes}
                                </Typography>
                              </>
                            )}
                            {record.rejectionNotes && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  Rejection notes
                                </Typography>
                                <Typography variant="body2" color="error">
                                  {record.rejectionNotes}
                                </Typography>
                              </>
                            )}
                            {record.approvedBy && typeof record.approvedBy === 'object' && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Approved by{' '}
                                {`${record.approvedBy.firstName || ''} ${record.approvedBy.lastName || ''}`.trim() || '—'}
                                {record.approvedAt ? ` · ${new Date(record.approvedAt).toLocaleString()}` : ''}
                              </Typography>
                            )}
                            {(record.statusHistory || []).length > 0 && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  Status History
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                  {record.statusHistory.map((entry, idx) => (
                                    <Typography key={idx} component="li" variant="body2" color="text.secondary">
                                      {entry.status}
                                      {entry.notes ? ` — ${entry.notes}` : ''} (
                                      {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : 'N/A'})
                                    </Typography>
                                  ))}
                                </Box>
                              </>
                            )}
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AttachFile />}
                                onClick={() => handleOpenDocumentsForRecord(record)}
                              >
                                Manage Documents
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={archiveFieldsDialogOpen} onClose={() => setArchiveFieldsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Archive fields in receiving history</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select item-level fields below to archive (hide) from the receiving history details. Data remains available in
            exports and backend.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('orderedQty')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'orderedQty'])] : prev.filter((k) => k !== 'orderedQty')
                    )
                  }
                />
              }
              label="Ordered quantity"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('description')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked
                        ? [...new Set([...prev, 'description'])]
                        : prev.filter((k) => k !== 'description')
                    )
                  }
                />
              }
              label="Description"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('receivedQty')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'receivedQty'])] : prev.filter((k) => k !== 'receivedQty')
                    )
                  }
                />
              }
              label="Received quantity"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('unit')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'unit'])] : prev.filter((k) => k !== 'unit')
                    )
                  }
                />
              }
              label="Unit"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('remainingQty')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked
                        ? [...new Set([...prev, 'remainingQty'])]
                        : prev.filter((k) => k !== 'remainingQty')
                    )
                  }
                />
              }
              label="Remaining quantity"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('condition')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'condition'])] : prev.filter((k) => k !== 'condition')
                    )
                  }
                />
              }
              label="Condition"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('storeType')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'storeType'])] : prev.filter((k) => k !== 'storeType')
                    )
                  }
                />
              }
              label="Store type"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('location')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'location'])] : prev.filter((k) => k !== 'location')
                    )
                  }
                />
              }
              label="Location"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('certificateNumber')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked
                        ? [...new Set([...prev, 'certificateNumber'])]
                        : prev.filter((k) => k !== 'certificateNumber')
                    )
                  }
                />
              }
              label="Certificate number"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('batchSerial')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'batchSerial'])] : prev.filter((k) => k !== 'batchSerial')
                    )
                  }
                />
              }
              label="Batch / Serial"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('shelfLife')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'shelfLife'])] : prev.filter((k) => k !== 'shelfLife')
                    )
                  }
                />
              }
              label="Shelf life upto"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHistoryFieldArchived('remarks')}
                  onChange={(e) =>
                    setArchivedHistoryFields((prev) =>
                      e.target.checked ? [...new Set([...prev, 'remarks'])] : prev.filter((k) => k !== 'remarks')
                    )
                  }
                />
              }
              label="Remarks"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveFieldsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={approvalDialog.open} onClose={approvalLoading ? undefined : closeApprovalDialog} maxWidth="xl" fullWidth>
        <DialogTitle>
          {approvalDialog.loanReceivingPurpose === 'LOAN_IN_RETURN'
            ? 'Review loan-in return'
            : approvalDialog.loanReceivingPurpose === 'LOAN_OUT_RETURN'
              ? 'Review loan-out return'
              : approvalDialog.loanReceivingPurpose === 'LOAN_IN'
                ? 'Review loan-in receiving'
                : 'Review receiving'}
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: 'auto' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {approvalDialog.loanReceivingPurpose === 'LOAN_IN_RETURN'
              ? 'Adjust received quantities and inspection details as needed, then approve to return borrowed parts from store inventory to the recipient using the loan reference—or reject to send back to the submitter.'
              : approvalDialog.loanReceivingPurpose === 'LOAN_OUT_RETURN'
                ? 'Adjust received quantities and inspection details as needed, then approve to post loan-out return quantities back to store inventory—or reject to send back to the submitter.'
                : approvalDialog.loanReceivingPurpose === 'LOAN_IN'
                  ? 'Adjust received quantities and inspection details as needed, then approve to post borrowed parts to store inventory using the loan reference (no purchase order)—or reject to send back to the submitter.'
                  : 'Adjust received quantities and inspection details as needed, then approve to post to inventory and purchase order totals—or reject to send back to the submitter.'}
            {' '}
            {approvalDialog.hasPurchaseOrder
              ? 'Only PO line items may be received — remove incorrect lines before approving.'
              : 'Remove incorrect lines or add omitted parts before approving.'}
          </Typography>
          {!approvalDialog.hasPurchaseOrder && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              disabled={approvalLoading}
              onClick={handleApprovalAddItem}
            >
              Add part
            </Button>
          </Box>
          )}
          <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table
              size="small"
              sx={{
                minWidth: 1680,
                tableLayout: 'auto',
                '& .MuiTableCell-root': { verticalAlign: 'top' },
                '& .MuiInputBase-root': { fontSize: '0.875rem' },
                '& .MuiInputBase-input': { overflow: 'visible', textOverflow: 'clip' },
                '& .MuiSelect-select': { overflow: 'visible', textOverflow: 'clip', whiteSpace: 'normal' }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 160 }}>Part number</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Reference part number</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Description</TableCell>
                  <TableCell sx={{ minWidth: 96 }}>Ordered qty</TableCell>
                  <TableCell sx={{ minWidth: 112 }}>
                    {approvalDialog.loanReceivingPurpose === 'LOAN_IN_RETURN'
                      || approvalDialog.loanReceivingPurpose === 'LOAN_OUT_RETURN'
                      ? 'Return qty'
                      : 'Received qty'}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Unit</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Condition</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Store Type</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Location</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>Certificate Number</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Batch</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Serial</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Shelf life</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Remarks</TableCell>
                  <TableCell sx={{ minWidth: 120 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvalItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No parts on this submission. Use Add part to add lines for inspection.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {approvalItems.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ minWidth: 160, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {row._reviewAdded ? (
                        <TextField
                          fullWidth
                          size="small"
                          required
                          placeholder="Part number"
                          value={row.partNumber || ''}
                          onChange={(e) => handleApprovalItemChange(index, 'partNumber', e.target.value)}
                          inputProps={{ style: { fontFamily: 'monospace' } }}
                        />
                      ) : (
                        <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                          {row.partNumber || '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 160, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {row._reviewAdded ? (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Reference part number"
                          value={row.referencePartNumber || ''}
                          onChange={(e) => handleApprovalItemChange(index, 'referencePartNumber', e.target.value)}
                          inputProps={{ style: { fontFamily: 'monospace' } }}
                        />
                      ) : (
                        <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                          {row.referencePartNumber || '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 220, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {row._reviewAdded ? (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Description"
                          value={row.description || ''}
                          onChange={(e) => handleApprovalItemChange(index, 'description', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                          {row.description || '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 96 }}>
                      {row._reviewAdded ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          inputProps={{ min: 0.001, step: 'any' }}
                          value={row.quantity ?? 1}
                          onChange={(e) =>
                            handleApprovalItemChange(index, 'quantity', parseFloat(e.target.value) || 1)
                          }
                        />
                      ) : (
                        <Typography variant="body2">{row.quantity ?? '—'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 112 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        value={row.receivedQuantity}
                        onChange={(e) =>
                          handleApprovalItemChange(index, 'receivedQuantity', parseFloat(e.target.value) || 0)
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {approvalDialog.hasPurchaseOrder ? (
                        <Typography variant="body2">
                          {unitMetricLabel(row.unit || 'EACH')}
                        </Typography>
                      ) : (
                        <Select
                          fullWidth
                          size="small"
                          value={row.unit || 'EACH'}
                          onChange={(e) => handleApprovalItemChange(index, 'unit', e.target.value)}
                        >
                          {unitSelectOptions(row.unit).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unitMetricLabel(unit)}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Select
                        fullWidth
                        size="small"
                        value={partConditionSelectValue(row.condition)}
                        onChange={(e) => handleApprovalItemChange(index, 'condition', e.target.value)}
                      >
                        {RECEIVING_CONDITION_SELECT_VALUES.map((v) => (
                          <MenuItem key={v} value={v}>{PART_CONDITION_LABELS[v] || v}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Select
                        fullWidth
                        size="small"
                        displayEmpty
                        value={normalizeStoreTypeValue(row.storeType)}
                        onChange={(e) => handleApprovalItemChange(index, 'storeType', e.target.value)}
                        renderValue={(selected) => {
                          const opt = STORE_TYPES.find((t) => t.value === selected);
                          return opt ? opt.label : '\u00a0';
                        }}
                      >
                        <MenuItem value="">
                          <em>Select store</em>
                        </MenuItem>
                        {STORE_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ minWidth: 260, verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Warehouse"
                          value={row.location?.warehouse || ''}
                          onChange={(e) => handleApprovalLocationChange(index, 'warehouse', e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Shelf"
                            value={row.location?.shelf || ''}
                            onChange={(e) => handleApprovalLocationChange(index, 'shelf', e.target.value)}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Bin"
                            value={row.location?.bin || ''}
                            onChange={(e) => handleApprovalLocationChange(index, 'bin', e.target.value)}
                          />
                        </Box>
                        <TextField
                          fullWidth
                          size="small"
                          label="Location note"
                          value={row.locationText || ''}
                          onChange={(e) => handleApprovalItemChange(index, 'locationText', e.target.value)}
                          inputProps={{ style: { whiteSpace: 'normal' } }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 280, verticalAlign: 'top' }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.certificateNumber || ''}
                        onChange={(e) => handleApprovalItemChange(index, 'certificateNumber', e.target.value)}
                        inputProps={{ style: { whiteSpace: 'normal' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.batchNumber || ''}
                        onChange={(e) => handleApprovalItemChange(index, 'batchNumber', e.target.value)}
                        inputProps={{ style: { whiteSpace: 'nowrap' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.serialNumber || ''}
                        onChange={(e) => handleApprovalItemChange(index, 'serialNumber', e.target.value)}
                        inputProps={{ style: { whiteSpace: 'nowrap' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      {row.shelfLifeNotApplicable ? (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                          N/A
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          value={row.shelfLifeUpto || ''}
                          onChange={(e) => handleApprovalItemChange(index, 'shelfLifeUpto', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 260, verticalAlign: 'top' }}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={row.remarks || ''}
                        onChange={(e) => handleApprovalItemChange(index, 'remarks', e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 120 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title="Line item details">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Edit />}
                              disabled={approvalLoading}
                              onClick={() => openApprovalLineFormDialog(index)}
                            >
                              Details
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove line">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={approvalLoading}
                              onClick={() => handleApprovalRemoveItem(index)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Discrepancies"
            value={approvalDiscrepancies}
            onChange={(e) => setApprovalDiscrepancies(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Inspector notes"
            value={approvalInspectorNotes}
            onChange={(e) => setApprovalInspectorNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Rejection notes (optional — sent with reject)"
            value={approvalRejectionNotes}
            onChange={(e) => setApprovalRejectionNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalDialog} disabled={approvalLoading}>
            Cancel
          </Button>
          <Button color="error" variant="outlined" disabled={approvalLoading} onClick={handleApprovalReject}>
            Reject
          </Button>
          <Button
            variant="contained"
            disabled={approvalLoading}
            onClick={handleApprovalApprove}
          >
            Approve and apply
          </Button>
        </DialogActions>
      </Dialog>

      <InventoryItemFormDialog
        open={approvalLineFormDialog.open}
        onClose={closeApprovalLineFormDialog}
        submitMode="return"
        initialLine={
          approvalLineFormDialog.rowIndex != null
            ? approvalItems[approvalLineFormDialog.rowIndex]
            : null
        }
        headerAircraft={approvalDialog.headerAircraft}
        aircraftList={aircraftList}
        lockPartNumber={approvalDialog.hasPurchaseOrder && !approvalItems[approvalLineFormDialog.rowIndex]?._reviewAdded}
        readOnlyOrderedQty={
          approvalLineFormDialog.rowIndex != null && approvalDialog.hasPurchaseOrder
            ? approvalItems[approvalLineFormDialog.rowIndex]?.quantity
            : null
        }
        showCertificateFields
        onReturn={handleApprovalLineFormReturn}
      />

      <Dialog
        open={poFieldDocsDialog.open}
        onClose={() => setPoFieldDocsDialog({ open: false, poId: '', category: '', label: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{poFieldDocsDialog.label ? `${poFieldDocsDialog.label} Documents` : 'Field Documents'}</DialogTitle>
        <DialogContent dividers>
          {getPoDocsByCategory(poFieldDocsDialog.poId, poFieldDocsDialog.category).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No documents uploaded for this field.</Typography>
          ) : (
            <MuiTable size="small">
              <MuiTableHead>
                <MuiTableRow>
                  <MuiTableCell>Name</MuiTableCell>
                  <MuiTableCell>Uploaded At</MuiTableCell>
                  <MuiTableCell align="right">Actions</MuiTableCell>
                </MuiTableRow>
              </MuiTableHead>
              <MuiTableBody>
                {getPoDocsByCategory(poFieldDocsDialog.poId, poFieldDocsDialog.category).map((doc) => (
                  <MuiTableRow key={doc._id}>
                    <MuiTableCell>{doc.originalName || doc.filename}</MuiTableCell>
                    <MuiTableCell>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : '—'}</MuiTableCell>
                    <MuiTableCell align="right">
                      <IconButton
                        size="small"
                        disabled={downloadingDocKey === doc._id}
                        onClick={() => handleDownloadAttachment(
                          `/api/store/purchase-orders/${poFieldDocsDialog.poId}/documents/${doc._id}/download`,
                          doc.originalName || doc.filename,
                          doc._id
                        )}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handlePoRefDocDelete(poFieldDocsDialog.poId, poFieldDocsDialog.category, doc._id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </MuiTableCell>
                  </MuiTableRow>
                ))}
              </MuiTableBody>
            </MuiTable>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoFieldDocsDialog({ open: false, poId: '', category: '', label: '' })}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyDeleteDialog.open}
        onClose={() => !historyDeleteLoading && setHistoryDeleteDialog({ open: false, targets: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {historyDeleteDialog.targets.length > 1
            ? `Delete ${historyDeleteDialog.targets.length} receiving records`
            : 'Delete receiving record'}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const approvedTargets = historyDeleteDialog.targets.filter((r) => r.workflowStatus === 'APPROVED');
            const hasApproved = approvedTargets.length > 0;
            return (
              <>
                <Typography variant="body2" sx={{ mb: historyDeleteDialog.targets.length > 1 ? 1.5 : 0 }}>
                  {historyDeleteDialog.targets.length > 1
                    ? 'Permanently delete the selected receiving records? This cannot be undone.'
                    : (
                      <>
                        Permanently delete receiving record <strong>{recordProcurementReference(historyDeleteDialog.targets[0])}</strong>?
                        This cannot be undone.
                      </>
                    )}
                </Typography>
                {hasApproved && (
                  <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
                    {approvedTargets.length === 1
                      ? 'This record has been approved. Deleting it will reverse inventory quantities and undo purchase order receiving.'
                      : `${approvedTargets.length} approved record(s) are included. Deleting them will reverse inventory quantities and undo purchase order receiving.`}
                  </Typography>
                )}
              </>
            );
          })()}
          {historyDeleteDialog.targets.length > 1 && (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {historyDeleteDialog.targets.map((record) => (
                <Typography key={record._id} component="li" variant="body2">
                  {recordProcurementReference(record)}
                  {' '}
                  ({getReceivingStatusInfo(record).label})
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setHistoryDeleteDialog({ open: false, targets: [] })}
            disabled={historyDeleteLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteReceivingHistory}
            disabled={historyDeleteLoading}
          >
            {historyDeleteLoading
              ? 'Deleting…'
              : historyDeleteDialog.targets.length > 1
                ? `Delete ${historyDeleteDialog.targets.length} records`
                : 'Delete record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={priTagDialog.open}
        onClose={() => setPriTagDialog({ open: false, record: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>PRI store acceptance tags</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Receiving approved. Download store acceptance tags: one PDF per serial when serials apply;
            one PDF per batch/lot (with that batch quantity) when multiple batches are on a line;
            otherwise a single PDF for batch/lot-only lines.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Part</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>PRI</TableCell>
                <TableCell align="right">Tag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(priTagDialog.record?.items || [])
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => Number(item.receivedQuantity) > 0 && collectLinePriNumbersForTag(item).length > 0)
                .map(({ item, idx }) => {
                  const key = `${priTagDialog.record._id}:${idx}`;
                  return (
                    <TableRow key={key}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{item.partNumber || '—'}</TableCell>
                      <TableCell>{item.receivedQuantity}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{formatLinePriDisplay(item)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PictureAsPdf />}
                          disabled={downloadingPriKey === key}
                          onClick={() => handleDownloadPriTagPdf(priTagDialog.record._id, idx, item)}
                        >
                          {downloadingPriKey === key ? 'Generating…' : 'Download PDF'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            disabled={Boolean(downloadingPriKey) || !(priTagDialog.record?.items || []).some(
              (item) => Number(item.receivedQuantity) > 0 && collectLinePriNumbersForTag(item).length > 0
            )}
            onClick={async () => {
              const record = priTagDialog.record;
              if (!record?._id) return;
              const lines = (record.items || [])
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => Number(item.receivedQuantity) > 0 && collectLinePriNumbersForTag(item).length > 0);
              let ok = 0;
              for (const { item, idx } of lines) {
                const success = await handleDownloadPriTagPdf(record._id, idx, item, { silent: true });
                if (success) ok += 1;
              }
              if (ok > 0) {
                showToast(`${ok} PRI tag PDF(s) downloaded`, 'success');
              }
            }}
          >
            Download all
          </Button>
          <Button onClick={() => setPriTagDialog({ open: false, record: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      <PurchaseOrderDeleteDialog
        open={poDeleteDialog.open}
        targets={poDeleteDialog.targets}
        onClose={() => setPoDeleteDialog({ open: false, targets: [] })}
        onDeleted={handlePoDeleted}
      />

      <StoreDocumentsDialog
        open={documentDialog.open}
        onClose={handleDocumentsClose}
        entityId={documentDialog.recordId}
        entityType="receiving"
        entityLabel={documentDialog.label}
      />
    </Box>
  );
}

export default ReceivingModule;

